import {
  ApiOutlined,
  ArrowDownOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
  BankOutlined,
  BellOutlined,
  BookOutlined,
  ClusterOutlined,
  DeleteOutlined,
  FileTextOutlined,
  FormOutlined,
  DownOutlined,
  InboxOutlined,
  LinkOutlined,
  MenuOutlined,
  MessageOutlined,
  PlusCircleOutlined,
  ReadOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UserOutlined
} from "@ant-design/icons";
import {
  Alert,
  App as AntApp,
  AutoComplete,
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  ConfigProvider,
  Descriptions,
  Divider,
  Drawer,
  Dropdown,
  Empty,
  Form,
  Input,
  Layout,
  List,
  Menu,
  Modal,
  Popconfirm,
  Progress,
  Radio,
  Result,
  Row,
  Select,
  Skeleton,
  Space,
  Spin,
  Statistic,
  Steps,
  Tabs,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import type { MenuProps, TableColumnsType } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Navigate,
  Outlet,
  Routes,
  Route,
  useLocation,
  useNavigate,
  useParams
} from "react-router-dom";
import {
  assignUserRole,
  acceptMyConsent,
  clearAuthToken,
  confirmStudentSignupPayment,
  confirmPasswordReset,
  createStudentChatSession,
  createUserAccount,
  deleteStudentChatSession,
  createSchool,
  createStudyMaterial,
  createSubject,
  deleteSchool,
  deleteSubject,
  createTopic,
  fetchCurrentUser,
  fetchGoogleAuthConfig,
  fetchSignupPaymentConfig,
  fetchAuditEvents,
  fetchAvailableSchools,
  fetchBootstrap,
  fetchMyConsent,
  fetchLegalDocuments,
  fetchChatHistory,
  fetchStudentChatSessionMessages,
  fetchStudentChatSessions,
  fetchGovernanceSummary,
  fetchQuizQuestions,
  fetchStudentNotebook,
  ensureNotebookChatReady,
  watchQuizViaSSE,
  pollQuizStatus,
  startQuizGeneration,
  fetchQuizById,
  fetchQuizHistory,
  saveQuizProgress,
  fetchRecommendedNextStep,
  fetchStudentBootstrap,
  fetchStudentUnderstanding,
  fetchTopics,
  getAuthToken,
  updateStudentSubjects,
  updateStudentChatSessionTitle,
  getConfiguredApiBaseLabel,
  getRoleWorkspaces,
  isPaymentRequiredError,
  login,
  loginWithGoogle,
  logout as logoutSession,
  registerStudent,
  registerStudentWithGoogle,
  requestMyPasswordReset,
  requestPasswordReset,
  requestMyDataDeletion,
  sendChatMessage,
  submitQuizAnswers,
  openNotificationSocket,
  updateMyEmail,
  updateMyProfile,
  updateLessonDependencies,
  deleteStudyMaterial,
  bulkDeleteStudyMaterials,
  setLessonPublication,
  updateStudyMaterial,
  updateSubject,
  updateTopic,
  uploadMaterialToOpenNotebook,
  retrySyncOpenNotebook,
  clearWrapperState
} from "../lib/api";
import type { AssessmentReadyNotification } from "../lib/api";
import { TypeformQuiz } from "../components/TypeformQuiz";
import type {
  ApiHealth,
  AuditEvent,
  ChatMessage,
  GapSignal,
  GovernanceSummary,
  Lesson,
  NotebookRecord,
  QuizQuestion,
  RecommendedNextStep,
  School,
  StudyMaterial,
  StudentUnderstandingProfile,
  StudentChatSession,
  Subject,
  Topic,
  UserAccount
} from "../lib/types";
import {
  initializeIntelligence,
  processStudentInput,
  processQuizAnswer,
  getMasteryVisualization,
  getTopicMastery,
  type IntelligenceState
} from "../lib/intelligence-service";
import {
  getMasteryColor,
  getMasteryLevel,
  getMasteryLabel,
  initializeBKTForTopic,
  updateBKTMastery,
  type BKTParams
} from "../lib/bkt-engine";
import { appTheme, brandTokens } from "../design-system/theme";
import { getSeverityAccent, getSeverityTagColor, surfaceStyles } from "../design-system/utils";
import * as katex from "katex";
import "katex/dist/katex.min.css";

const INTELLIGENCE_STORAGE_KEY = "classroom_notebook_intelligence_state";

function serializeIntelligenceState(state: IntelligenceState): string {
  return JSON.stringify({
    ...state,
    assessments: Array.from(state.assessments.entries()),
    calibratedQuestions: Array.from(state.calibratedQuestions.entries())
  });
}

function deserializeIntelligenceState(json: string): IntelligenceState | null {
  try {
    const parsed = JSON.parse(json);
    return {
      ...parsed,
      assessments: new Map(parsed.assessments || []),
      calibratedQuestions: new Map(parsed.calibratedQuestions || [])
    };
  } catch {
    return null;
  }
}

function saveIntelligenceState(state: IntelligenceState): void {
  try {
    localStorage.setItem(INTELLIGENCE_STORAGE_KEY, serializeIntelligenceState(state));
  } catch (e) {
    console.warn("Failed to save intelligence state to localStorage:", e);
  }
}

function loadIntelligenceState(): IntelligenceState | null {
  try {
    const stored = localStorage.getItem(INTELLIGENCE_STORAGE_KEY);
    if (!stored) return null;
    return deserializeIntelligenceState(stored);
  } catch (e) {
    console.warn("Failed to load intelligence state from localStorage:", e);
    return null;
  }
}

function clearIntelligenceState(): void {
  try {
    localStorage.removeItem(INTELLIGENCE_STORAGE_KEY);
  } catch (e) {
    console.warn("Failed to clear intelligence state from localStorage:", e);
  }
}

function getMaterialTypeFromFile(file: File): StudyMaterial["materialType"] {
  const name = file.name.toLowerCase();
  if (name.endsWith(".pdf")) return "pdf";
  if (name.endsWith(".ppt") || name.endsWith(".pptx")) return "slides";
  if ([".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg"].some(s => name.endsWith(s))) return "image";
  if (name.includes("textbook")) return "textbook";
  return "document";
}

const { Header, Content, Sider } = Layout;
const { Title, Paragraph, Text } = Typography;

function getStudentSignupPaymentReference(search: string): string | null {
  const params = new URLSearchParams(search);
  return params.get("student_signup_reference") || params.get("reference");
}

type GoogleCredentialResponse = {
  credential?: string;
};

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (options: {
            client_id: string;
            callback: (response: GoogleCredentialResponse) => void;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: { theme?: string; size?: string; width?: string; text?: string }
          ) => void;
        };
      };
    };
  }
}

type WorkspaceKey =
  | "profile"
  | "materials"
  | "subjects"
  | "topic-graph"
  | "schools"
  | "accounts"
  | "api-status"
  | "understanding-map"
  | "practice"
  | "chat";

type EndOfSessionSummary = {
  subjectId: string;
  score: number;
  total: number;
  strengthenedTopics: string[];
  needsAttentionTopics: string[];
  nextStepTitle?: string;
  nextStepReason?: string;
};

const workspaceLabels: Record<WorkspaceKey, string> = {
  profile: "Profile",
  materials: "Materials",
  subjects: "Subjects",
  "topic-graph": "Topic Graph",
  schools: "Schools",
  accounts: "Accounts",
  "api-status": "API Status",
  "understanding-map": "Understanding Map",
  practice: "Practice",
  chat: "Chat"
};

function withoutOverview(workspaces: readonly string[]) {
  return workspaces.filter((workspace) => workspace !== "overview");
}

function clampPercent(percent: number) {
  return Math.max(0, Math.min(100, percent));
}

function ProtectedRoute({ children, user }: { children: React.ReactNode; user: UserAccount | null }) {
  const location = useLocation();
  
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return <>{children}</>;
}

function WorkspaceRouter({ user }: { user: UserAccount | null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname.slice(1) as WorkspaceKey;
  
  const roleWorkspaces: string[] = useMemo(
    () => (user ? withoutOverview([...getRoleWorkspaces(user.role)]) : []),
    [user?.role]
  );
  
  if (!path || !roleWorkspaces.includes(path)) {
    return <Navigate to={`/${roleWorkspaces[0] || "profile"}`} replace />;
  }
  
  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider width={260} breakpoint="lg" collapsedWidth="0">
        <div style={{ padding: 24 }}>
          <Title level={3} style={{ color: "#fff", margin: 0 }}>
            Kwazi
          </Title>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[path]}
          items={roleWorkspaces.map((key) => ({
            key,
            icon: workspaceIcon(key as WorkspaceKey),
            label: workspaceLabels[key as WorkspaceKey]
          }))}
          onClick={({ key }) => navigate(`/${key}`)}
        />
      </Sider>
      <Layout>
        <Header>
          <Row gutter={[16, 16]} align="middle" justify="space-between">
            <Col>
              {path !== "profile" && (
                <Space direction="vertical" size={2}>
                  <Text type="secondary">Workspace</Text>
                  <Title level={4} style={{ margin: 0 }}>
                    {workspaceLabels[path]}
                  </Title>
                </Space>
              )}
            </Col>
            <Col>
              {/* Placeholder for header content - will be rendered in content area */}
            </Col>
          </Row>
        </Header>
        <Content style={{ margin: "16px" }}>
          <div>{/* Workspace content rendered via Outlet */}</div>
        </Content>
      </Layout>
    </Layout>
  );
}

export function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [authUser, setAuthUser] = useState<UserAccount | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [accounts, setAccounts] = useState<UserAccount[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [notebooks, setNotebooks] = useState<NotebookRecord[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [subjectTopicsCache, setSubjectTopicsCache] = useState<Record<string, Topic[]>>({});
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [materials, setMaterials] = useState<StudyMaterial[]>([]);
  const [apiHealth, setApiHealth] = useState<ApiHealth[]>([]);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [chatSessions, setChatSessions] = useState<StudentChatSession[]>([]);
  const [activeChatSessionId, setActiveChatSessionId] = useState<string | null>(null);
  const [chatSessionsLoading, setChatSessionsLoading] = useState(false);
  const [chatSubmitting, setChatSubmitting] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [quizGenerating, setQuizGenerating] = useState(false);
  const [quizSessionActive, setQuizSessionActive] = useState(false);
  const [currentQuizId, setCurrentQuizId] = useState<string | null>(null);
  const [quizHistory, setQuizHistory] = useState<any[]>([]);
  const [sessionSummary, setSessionSummary] = useState<EndOfSessionSummary | null>(null);
  const [assessmentNotification, setAssessmentNotification] = useState<{
    unread: boolean;
    questionCount: number;
    topicName?: string;
  } | null>(null);

  const [governanceSummary, setGovernanceSummary] = useState<GovernanceSummary | null>(null);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [understanding, setUnderstanding] = useState<StudentUnderstandingProfile | null>(
    null
  );
  const [recommendedNextStep, setRecommendedNextStep] = useState<RecommendedNextStep | null>(null);
  const [recommendedNextStepLoading, setRecommendedNextStepLoading] = useState(false);
  const [intelligence, setIntelligence] = useState<IntelligenceState | null>(null);
  const [selectedNotebookId, setSelectedNotebookId] = useState("");
  const [selectedSubjectId, setSelectedSubjectId] = useState("subject-life-sciences");
  const [selectedLessonId, setSelectedLessonId] = useState("lesson-big-data");
  const [activeWorkspace, setActiveWorkspace] = useState<WorkspaceKey>("profile");
  const [schoolModalOpen, setSchoolModalOpen] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [studentEnrolledSubjectIds, setStudentEnrolledSubjectIds] = useState<string[]>([]);
  const [notebookIds, setNotebookIds] = useState<Record<string, string>>({});
  const prewarmedChatContextRef = useRef<Record<string, boolean>>({});
  const activeWorkspaceRef = useRef<WorkspaceKey>(activeWorkspace);

  useEffect(() => {
    activeWorkspaceRef.current = activeWorkspace;
  }, [activeWorkspace]);

  useEffect(() => {
    if (!authUser || authUser.role !== "student" || !selectedSubjectId) return;
    if (activeWorkspace !== "practice" && activeWorkspace !== "chat") return;
    if (notebookIds[selectedSubjectId]) return;

    fetchStudentNotebook(authUser.id, selectedSubjectId).then((id) => {
      if (id) {
        setNotebookIds((prev) => ({ ...prev, [selectedSubjectId]: id }));
      }
    });
  }, [activeWorkspace, selectedSubjectId]);

  useEffect(() => {
    if (!authUser || authUser.role !== "student" || !selectedSubjectId) return;
    if (activeWorkspace !== "practice" && activeWorkspace !== "chat") return;

    const cacheKey = `${authUser.id}:${selectedSubjectId}`;
    const hasProfileContext = Boolean(understanding && understanding.studentId === authUser.id);
    const profileCacheKey = `${cacheKey}:${hasProfileContext ? "with-profile" : "no-profile"}`;
    if (prewarmedChatContextRef.current[profileCacheKey]) return;

    prewarmedChatContextRef.current[profileCacheKey] = true;

    void ensureNotebookChatReady(authUser.id, selectedSubjectId, understanding).catch(() => {
      prewarmedChatContextRef.current[profileCacheKey] = false;
    });
  }, [authUser, activeWorkspace, selectedSubjectId, understanding]);

  useEffect(() => {
    if (!authUser || authUser.role !== "student" || !selectedSubjectId) {
      setRecommendedNextStep(null);
      setRecommendedNextStepLoading(false);
      return;
    }

    let cancelled = false;
    setRecommendedNextStepLoading(true);
    void fetchRecommendedNextStep(authUser.id, selectedSubjectId)
      .then((nextStep) => {
        if (!cancelled) setRecommendedNextStep(nextStep);
      })
      .catch(() => {
        if (!cancelled) setRecommendedNextStep(null);
      })
      .finally(() => {
        if (!cancelled) setRecommendedNextStepLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authUser, selectedSubjectId, understanding]);

  function getErrorMessage(error: unknown) {
    return error instanceof Error ? error.message : "Something went wrong.";
  }

  async function loadBootstrap(preferredNotebookId?: string, showProgressMessage = true, authUser?: UserAccount) {
    setBootstrapping(true);
    setBootstrapError(null);

    try {
      const bootstrap = await fetchBootstrap(preferredNotebookId, authUser?.role === "student");
      const currentUser = authUser;
      setAccounts(bootstrap.accounts);
      setSchools(bootstrap.schools);
      setNotebooks(bootstrap.notebooks);
      setSubjects(bootstrap.subjects);
      setTopics(bootstrap.topics);
      setLessons(bootstrap.lessons);
      setMaterials(bootstrap.materials);
      setApiHealth(bootstrap.apiHealth);
      if (currentUser?.role !== "student") {
        setAuditEvents(await fetchAuditEvents());
      }

      const studentId = currentUser?.id;
      const userEnrolledSubjectIds = (currentUser as any)?.enrolledSubjectIds || [];
      if (userEnrolledSubjectIds.length > 0) {
        setStudentEnrolledSubjectIds(userEnrolledSubjectIds);
        setSelectedSubjectId(userEnrolledSubjectIds[0]);
      }
      console.log('enrolledSubjectIds from /me:', userEnrolledSubjectIds);
      
      const savedState = loadIntelligenceState();
      if (savedState && savedState.masteryState.studentId === studentId) {
        const topicIds = new Set(bootstrap.topics.map(t => t.id));
        const validMastery: Record<string, typeof savedState.masteryState.topicMastery[string]> = {};
        for (const [topicId, params] of Object.entries(savedState.masteryState.topicMastery)) {
          if (topicIds.has(topicId)) {
            validMastery[topicId] = params;
          }
        }
        const intelState: IntelligenceState = {
          ...savedState,
          masteryState: {
            ...savedState.masteryState,
            topicMastery: validMastery
          },
          lastUpdated: new Date().toISOString()
        };
        setIntelligence(intelState);
        if (showProgressMessage) {
          message.info("Loaded your progress from previous session.");
        }
        // Also fetch server-side understanding profile for the overview page
        fetchStudentUnderstanding(studentId).then(profile => {
          if (profile) setUnderstanding(profile);
        }).catch(() => {});
      } else if (studentId) {
        const intelState = initializeIntelligence(studentId, bootstrap.topics);
        // Sync existing server-side BKT data into client-side intelligence
        fetchStudentUnderstanding(studentId).then(profile => {
          if (profile) {
            setUnderstanding(profile);
            if (profile?.conceptMastery?.length) {
              const updated: typeof intelState = {
                ...intelState,
                masteryState: {
                  ...intelState.masteryState,
                  topicMastery: { ...intelState.masteryState.topicMastery }
                }
              };
              for (const cm of profile.conceptMastery) {
                const tid = cm.topicId || cm.conceptId;
                if (tid && updated.masteryState.topicMastery[tid]) {
                  updated.masteryState.topicMastery[tid] = {
                    ...updated.masteryState.topicMastery[tid],
                    pKnown: cm.confidence,
                    attempts: 1,
                    lastUpdated: new Date().toISOString()
                  };
                }
              }
              setIntelligence(updated);
            }
          }
        }).catch(() => { /* non-blocking */ });
        setIntelligence(intelState);
      }

if (studentId && accounts.find(a => a.id === studentId)?.role === "student") {
        setStudentEnrolledSubjectIds(userEnrolledSubjectIds);
        if (userEnrolledSubjectIds.length > 0) {
          setSelectedSubjectId(userEnrolledSubjectIds[0]);
        }
      }
    } catch (error) {
      const detail = getErrorMessage(error);
      setBootstrapError(detail);
      message.error(detail);
    } finally {
      setBootstrapping(false);
    }
  }

  async function loadStudentBootstrap(studentUser: UserAccount, showProgressMessage = true) {
    setBootstrapping(true);
    setBootstrapError(null);

    try {
      const bootstrap = await fetchStudentBootstrap(studentUser.id);
      const enrolled = bootstrap.enrolledSubjectIds || [];
      const selectedSubject = enrolled[0] || "";
      setAccounts([]);
      setSchools([]);
      setNotebooks([]);
      setSubjects(bootstrap.subjects);
      setTopics(bootstrap.topics);
      setLessons([]);
      setMaterials(bootstrap.materials);
      setApiHealth([{ domain: "Wrapper API", status: "ready", notes: "Connected to backend" }]);
      setAuditEvents([]);
      setStudentEnrolledSubjectIds(enrolled);
      setSelectedSubjectId(selectedSubject);
      setUnderstanding(bootstrap.understanding);
      setAuthUser((user) => (user ? { ...user, enrolledSubjectIds: enrolled } : user));

      const savedState = loadIntelligenceState();
      if (savedState && savedState.masteryState.studentId === studentUser.id) {
        const topicIds = new Set(bootstrap.topics.map((topic) => topic.id));
        const validMastery: Record<string, typeof savedState.masteryState.topicMastery[string]> = {};
        for (const [topicId, params] of Object.entries(savedState.masteryState.topicMastery)) {
          if (topicIds.has(topicId)) {
            validMastery[topicId] = params;
          }
        }
        setIntelligence({
          ...savedState,
          masteryState: {
            ...savedState.masteryState,
            topicMastery: validMastery,
          },
          lastUpdated: new Date().toISOString(),
        });
        if (showProgressMessage) {
          message.info("Loaded your progress from previous session.");
        }
      } else {
        setIntelligence(initializeIntelligence(studentUser.id, bootstrap.topics));
      }
    } catch (error) {
      const detail = getErrorMessage(error);
      setBootstrapError(detail);
      message.error(detail);
    } finally {
      setBootstrapping(false);
    }
  }

  async function runAction<T>(
    action: () => Promise<T>,
    options?: { successMessage?: string; errorMessage?: string }
  ) {
    try {
      const result = await action();
      if (options?.successMessage) {
        message.success(options.successMessage);
      }
      return result;
    } catch (error) {
      message.error(options?.errorMessage || getErrorMessage(error));
      return null;
    }
  }

  useEffect(() => {
    if (intelligence) {
      saveIntelligenceState(intelligence);
    }
  }, [intelligence]);

  const hydratedRef = useRef(false);

  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    async function hydrateSession() {
      if (getStudentSignupPaymentReference(location.search) || new URLSearchParams(location.search).get("student_signup_cancelled")) {
        clearAuthToken();
        setAuthUser(null);
        setAuthReady(true);
        setBootstrapping(false);
        return;
      }

      const user = await fetchCurrentUser();
      setAuthUser(user);
      setAuthReady(true);
      if (user) {
        if (user.role === "student") {
          await loadStudentBootstrap(user, true);
        } else {
          await loadBootstrap(undefined, true, user);
        }
        const path = location.pathname.slice(1);
        if (path && path !== "login" && path !== "/") {
          setActiveWorkspace(path as WorkspaceKey);
        }
      } else {
        if (getAuthToken()) {
          setAuthReady(true);
          return;
        }
        setBootstrapping(false);
        if (location.pathname !== "/login" && location.pathname !== "/signup") {
          navigate("/login", { replace: true });
        }
      }
    }

    void hydrateSession();
  }, []);

  const signupPaymentReferenceRef = useRef<string | null>(null);

  useEffect(() => {
    if (!authReady || authUser) return;

    const params = new URLSearchParams(location.search);
    const emailVerified = params.get("email_verified");
    if (emailVerified) {
      if (emailVerified === "1") {
        message.success("Email verified. You can now sign in.");
      } else {
        message.error("Email verification failed. Please use the latest verification email or request help.");
      }
      params.delete("email_verified");
      const nextSearch = params.toString();
      navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`, { replace: true });
      return;
    }

    const reference = getStudentSignupPaymentReference(location.search);
    if (params.get("student_signup_cancelled")) {
      message.info("Payment cancelled. Your account was not created.");
      navigate("/login", { replace: true });
      return;
    }

    if (!reference || signupPaymentReferenceRef.current === reference) return;
    signupPaymentReferenceRef.current = reference;
    void completePaidStudentSignUp(reference);
  }, [authReady, authUser, location.search]);

  useEffect(() => {
    async function reloadForNotebook() {
      if (!selectedNotebookId) {
        return;
      }

      try {
        const bootstrap = await fetchBootstrap(selectedNotebookId);
        setSchools(bootstrap.schools);
        setNotebooks(bootstrap.notebooks);
        setSubjects(bootstrap.subjects);
setTopics(bootstrap.topics);
        setLessons(bootstrap.lessons);
        setMaterials(bootstrap.materials);
        setApiHealth(bootstrap.apiHealth);
        if (currentUser?.role !== "student") {
          setAuditEvents(await fetchAuditEvents());
        }
        setSelectedSubjectId((current) =>
          bootstrap.subjects.some((subject) => subject.id === current)
            ? current
            : bootstrap.subjects[0]?.id || ""
        );
        setSelectedLessonId((current) =>
          bootstrap.lessons.some((lesson) => lesson.id === current)
            ? current
            : bootstrap.lessons[0]?.id || ""
        );
      } catch (error) {
        message.error(`Failed to load notebook data. ${getErrorMessage(error)}`);
      }
    }

    void reloadForNotebook();
  }, [selectedNotebookId]);

  const currentUser = authUser;
  const selectedSubject = useMemo(
    () => subjects.find((subject) => subject.id === selectedSubjectId) || subjects[0],
    [selectedSubjectId, subjects]
  );
  const visibleLessons = useMemo(
    () => {
      return lessons.filter((lesson) =>
        selectedSubjectId ? lesson.subjectId === selectedSubjectId : true
      );
    },
    [lessons, selectedSubjectId]
  );
  const enrolledSubjects = useMemo(
    () => subjects.filter((s) => studentEnrolledSubjectIds.includes(s.id)),
    [subjects, studentEnrolledSubjectIds]
  );
  
  const effectiveSubjects = currentUser?.role === "student" ? enrolledSubjects : subjects;

  useEffect(() => {
    if (currentUser?.role !== "student") return;
    if (enrolledSubjects.length === 0) {
      setSelectedSubjectId("");
      return;
    }
    if (!enrolledSubjects.some((subject) => subject.id === selectedSubjectId)) {
      setSelectedSubjectId(enrolledSubjects[0].id);
    }
  }, [currentUser?.role, enrolledSubjects, selectedSubjectId]);
  const selectedLesson = useMemo(
    () => visibleLessons.find((lesson) => lesson.id === selectedLessonId) || visibleLessons[0],
    [visibleLessons, selectedLessonId]
  );
  const roleWorkspaces = useMemo(
    () =>
      currentUser
        ? withoutOverview([...getRoleWorkspaces(currentUser.role)])
        : (["profile"] as const),
    [currentUser]
  );

  useEffect(() => {
    setActiveWorkspace((current) => {
      const validWorkspaces = ["profile", ...roleWorkspaces] as readonly string[];
      if (validWorkspaces.includes(current)) {
        return current;
      }
      return roleWorkspaces[0] as WorkspaceKey;
    });
  }, [roleWorkspaces]);

  useEffect(() => {
    const pathWorkspace = location.pathname.slice(1) as WorkspaceKey;
    if (!pathWorkspace) return;
    const validWorkspaces = ["profile", ...roleWorkspaces] as readonly string[];
    if (validWorkspaces.includes(pathWorkspace)) {
      setActiveWorkspace(pathWorkspace);
    }
  }, [location.pathname, roleWorkspaces]);

  useEffect(() => {
    setSelectedLessonId((current) =>
      visibleLessons.some((lesson) => lesson.id === current)
        ? current
        : visibleLessons[0]?.id || ""
    );
  }, [visibleLessons]);

  useEffect(() => {
    async function loadRoleScopedData() {
      if (!currentUser || !selectedLesson) {
        return;
      }

      const isStudent = currentUser.role === "student";
      try {
        const [history, quiz, nextGovernanceSummary, nextUnderstanding] = await Promise.all([
          isStudent
            ? Promise.resolve([] as ChatMessage[])
            : fetchChatHistory(selectedLesson.id),
          isStudent
            ? Promise.resolve([] as QuizQuestion[])
            : fetchQuizQuestions(selectedLesson.id, lessons, topics, materials),
          fetchGovernanceSummary(selectedLesson.id),
          fetchStudentUnderstanding(currentUser.id),
        ]);

        if (!isStudent) {
          setChatHistory(history);
        }
        if (!isStudent) {
          setQuizQuestions(quiz);
        }
        setGovernanceSummary(nextGovernanceSummary);
        setUnderstanding(nextUnderstanding);
      } catch (error) {
        message.error(`Failed to load role-specific data. ${getErrorMessage(error)}`);
      }
    }

    void loadRoleScopedData();
  }, [currentUser, selectedLesson, lessons, topics, materials]);

  useEffect(() => {
    if (!currentUser || currentUser.role !== "student" || !selectedSubjectId) {
      setChatSessions([]);
      setActiveChatSessionId(null);
      setChatHistory([]);
      return;
    }

    let cancelled = false;
    setChatSessionsLoading(true);
    fetchStudentChatSessions(currentUser.id, selectedSubjectId)
      .then(async (sessions) => {
        if (cancelled) return;
        let nextSessions = sessions;
        if (!nextSessions.length) {
          const created = await createStudentChatSession(currentUser.id, selectedSubjectId);
          nextSessions = [created];
        }
        if (cancelled) return;
        setChatSessions(nextSessions);
        setActiveChatSessionId(nextSessions[0]?.id || null);
      })
      .catch(() => {
        if (cancelled) return;
        setChatSessions([]);
        setActiveChatSessionId(null);
        setChatHistory([]);
      })
      .finally(() => {
        if (!cancelled) setChatSessionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentUser, selectedSubjectId]);

  useEffect(() => {
    if (!currentUser || currentUser.role !== "student" || !selectedSubjectId || !activeChatSessionId) {
      setChatHistory([]);
      return;
    }

    let cancelled = false;
    setChatHistory([]);
    fetchStudentChatSessionMessages(currentUser.id, selectedSubjectId, activeChatSessionId)
      .then((history) => {
        if (!cancelled) setChatHistory(history);
      })
      .catch(() => {
        if (!cancelled) setChatHistory([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser, selectedSubjectId, activeChatSessionId]);

  // Fetch quiz history whenever the user changes — independent of lesson context
  useEffect(() => {
    if (currentUser?.role === "student") {
      fetchQuizHistory(currentUser.id).then(setQuizHistory).catch(() => {});
    }
  }, [currentUser]);

  // Auto-save quiz progress to the server whenever answers change
  const progressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!quizSessionActive || !currentQuizId || !currentUser) return;
    if (Object.keys(quizAnswers).length === 0) return;

    if (progressTimerRef.current) clearTimeout(progressTimerRef.current);
    progressTimerRef.current = setTimeout(() => {
      saveQuizProgress(currentUser.id, currentQuizId, quizAnswers);
    }, 2000);

    return () => {
      if (progressTimerRef.current) clearTimeout(progressTimerRef.current);
    };
  }, [quizAnswers, quizSessionActive, currentQuizId, currentUser]);

  useEffect(() => {
    if (activeWorkspace === "practice" && assessmentNotification?.unread) {
      setAssessmentNotification((current) => current ? { ...current, unread: false } : current);
    }
  }, [activeWorkspace, assessmentNotification?.unread]);

  useEffect(() => {
    if (!currentUser || currentUser.role !== "student") return;

    let closed = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (closed) return;
      try {
        socket = openNotificationSocket((notification: AssessmentReadyNotification) => {
          if (notification.studentId !== currentUser.id) {
            return;
          }
          if (activeWorkspaceRef.current !== "practice") {
            setAssessmentNotification({
              unread: true,
              questionCount: notification.questionCount,
              topicName: notification.topicName
            });
          }
        });
        socket.addEventListener("close", () => {
          if (!closed) {
            reconnectTimer = setTimeout(connect, 3000);
          }
        });
      } catch {
        reconnectTimer = setTimeout(connect, 3000);
      }
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      socket?.close();
    };
  }, [currentUser?.id, currentUser?.role]);

  // Save progress immediately on exit/unmount
  useEffect(() => {
    return () => {
      if (progressTimerRef.current) clearTimeout(progressTimerRef.current);
    };
  }, []);

  const topicLookup = useMemo(
    () => new Map(topics.map((topic) => [topic.id, topic])),
    [topics]
  );

  useEffect(() => {
    setQuizSessionActive(false);
    setQuizQuestions([]);
    setQuizAnswers({});
  }, [selectedSubjectId]);

  const selectedTopic = selectedLesson ? topicLookup.get(selectedLesson.topicId) : undefined;
  const selectedMaterials = useMemo(
    () =>
      materials.filter((material) =>
        (selectedLesson?.sourceMaterialIds || []).includes(material.id)
      ),
    [materials, selectedLesson]
  );

  async function refreshBootstrap() {
    try {
      const bootstrap = await fetchBootstrap(selectedNotebookId || undefined);
      setAccounts(bootstrap.accounts);
      setSchools(bootstrap.schools);
      setNotebooks(bootstrap.notebooks);
      setSubjects(bootstrap.subjects);
      setTopics(bootstrap.topics);
      setLessons(bootstrap.lessons);
      setMaterials(bootstrap.materials);
      setApiHealth(bootstrap.apiHealth);
      if (currentUser?.role !== "student") {
        setAuditEvents(await fetchAuditEvents());
      }
      if (selectedLessonId) {
        setGovernanceSummary(await fetchGovernanceSummary(selectedLessonId));
      }
    } catch (error) {
      message.error(`Failed to refresh app data. ${getErrorMessage(error)}`);
    }
  }

  const menuItems: MenuProps["items"] = roleWorkspaces.map((key) => ({
    key,
    icon: workspaceIcon(key as WorkspaceKey),
    label: workspaceLabels[key as WorkspaceKey]
  }));
  const hasUnreadAssessment = currentUser?.role === "student" && Boolean(assessmentNotification?.unread);
  const accountMenuItems: MenuProps["items"] = [
    ...(currentUser?.role === "student" && assessmentNotification
      ? [
          {
            key: "assessment-ready",
            icon: <BellOutlined />,
            label: assessmentNotification.unread
              ? `Assessment ready${assessmentNotification.topicName ? `: ${assessmentNotification.topicName}` : ""}`
              : "Latest assessment",
          }
        ]
      : []),
    {
      key: "profile",
      label: "Profile"
    },
    {
      key: "logout",
      label: "Logout"
    }
  ];

  async function handleCreateMaterial(values: {
    title: string;
    excerpt: string;
    citation?: string;
    materialType: StudyMaterial["materialType"];
    sourceAuthority: StudyMaterial["sourceAuthority"];
    file: File;
    schoolId?: string | null;
    subjectId: string;
    topicIds: string[];
  }) {
    await runAction(
      async () => {
          const isStudent = currentUser?.role === "student";
          const material = await createStudyMaterial({
            subjectId: values.subjectId,
            topicIds: values.topicIds,
            title: values.title,
            materialType: values.materialType,
            sourceAuthority: isStudent ? "draft" : (values.sourceAuthority || "draft"),
            excerpt: values.excerpt,
            citation: values.citation,
            lessonId: selectedLesson?.id,
            schoolId: isStudent ? null : (values.schoolId || null),
            file: values.file
          });

        await refreshBootstrap();
      },
      { successMessage: "Study material created." }
    );
  }

  async function handleUpdateMaterial(
    materialId: string,
    patch: Partial<Omit<StudyMaterial, "id">>
  ) {
    await runAction(
      async () => {
        await updateStudyMaterial(materialId, patch);
        await refreshBootstrap();
      },
      { successMessage: "Study material updated." }
    );
  }

  async function handleDeleteMaterial(materialId: string) {
    await runAction(
      async () => {
        await deleteStudyMaterial(materialId);
        await refreshBootstrap();
      },
      { successMessage: "Study material deleted." }
    );
  }

  async function handleBulkDeleteMaterial(materialIds: string[]) {
    await runAction(
      async () => {
        await bulkDeleteStudyMaterials(materialIds);
        await refreshBootstrap();
      },
      { successMessage: `${materialIds.length} study material(s) deleted.` }
    );
  }

  async function handleCreateTopic(values: {
    name: string;
    subjectId: string;
    description: string;
    understandingGoal: string;
    prerequisiteTopicIds: string[];
  }) {
    await runAction(
      async () => {
        await createTopic(values);
        await refreshBootstrap();
      },
      { successMessage: "Topic created." }
    );
  }

  async function handleSaveTopic(topicId: string, values: {
    name: string;
    subjectId: string;
    description: string;
    understandingGoal: string;
    prerequisiteTopicIds: string[];
  }) {
    await runAction(
      async () => {
        await updateTopic(topicId, values);
        await refreshBootstrap();
      },
      { successMessage: "Topic updated." }
    );
  }

  async function handleSaveLessonDependencies(values: { lessonDependencyIds: string[] }) {
    if (!selectedLesson) {
      return;
    }

    await runAction(
      async () => {
        await updateLessonDependencies(selectedLesson.id, values.lessonDependencyIds);
        await refreshBootstrap();
      },
      { successMessage: "Lesson dependency map updated." }
    );
  }

  async function handleAssignRole(userId: string, role: UserAccount["role"]) {
    await runAction(
      async () => {
        await assignUserRole(userId, role);
        await refreshBootstrap();
      },
      { successMessage: "Role updated." }
    );
  }

  async function handleCreateSchool(values: {
    name: string;
    shortCode?: string;
    contactEmail?: string;
  }) {
    const result = await runAction(
      async () => {
        await createSchool(values);
        await refreshBootstrap();
      },
      { successMessage: "School saved." }
    );

    if (result !== null) {
      setSchoolModalOpen(false);
    }
  }

  async function handleCreateAccount(values: {
    fullName: string;
    email: string;
    role: UserAccount["role"];
    organizationName: string;
  }) {
    const result = await runAction(
      async () => {
        await createUserAccount(values);
        await refreshBootstrap();
      },
      { successMessage: "Account created." }
    );

    if (result !== null) {
      setAccountModalOpen(false);
    }
  }

  async function handleDeleteSchool(schoolId: string) {
    await runAction(
      async () => {
        await deleteSchool(schoolId);
        await refreshBootstrap();
      },
      { successMessage: "School deleted." }
    );
  }

  async function handleCreateSubject(values: {
    name: string;
    gradeBand?: string;
    description?: string;
  }) {
    await runAction(
      async () => {
        await createSubject(values);
        await refreshBootstrap();
      },
      { successMessage: "Subject created." }
    );
  }

  async function handleUpdateSubject(
    subjectId: string,
    patch: Partial<Omit<Subject, "id">>
  ) {
    await runAction(
      async () => {
        await updateSubject(subjectId, patch);
        await refreshBootstrap();
      },
      { successMessage: "Subject updated." }
    );
  }

  async function handleDeleteSubject(subjectId: string) {
    await runAction(
      async () => {
        await deleteSubject(subjectId);
        await refreshBootstrap();
      },
      { successMessage: "Subject deleted." }
    );
  }

  async function handleLogin(values: { email: string; password: string }) {
    let user: UserAccount | null = null;
    try {
      const nextUser = await login(values.email, values.password);
      setAuthUser(nextUser);
      if (nextUser.role === "student") {
        await loadStudentBootstrap(nextUser, false);
      } else {
        await loadBootstrap(undefined, false, nextUser);
      }
      message.success("Logged in.");
      user = nextUser;
    } catch (error) {
      if (isPaymentRequiredError(error) && error.checkoutUrl) {
        message.info("Payment is required. Redirecting to payment...");
        window.location.href = error.checkoutUrl;
        return;
      }
      message.error(getErrorMessage(error));
    }

    if (user) {
      const firstWorkspace = withoutOverview([...getRoleWorkspaces(user.role)])[0] || "profile";
      navigate(`/${firstWorkspace}`, { replace: true });
    } else {
      setAuthUser(null);
    }
  }

  async function handleGoogleLogin(credential: string) {
    let user: UserAccount | null = null;
    try {
      const nextUser = await loginWithGoogle(credential);
      setAuthUser(nextUser);
      if (nextUser.role === "student") {
        await loadStudentBootstrap(nextUser, false);
      } else {
        await loadBootstrap(undefined, false, nextUser);
      }
      message.success("Signed in with Google.");
      user = nextUser;
    } catch (error) {
      if (isPaymentRequiredError(error) && error.checkoutUrl) {
        message.info("Payment is required. Redirecting to payment...");
        window.location.href = error.checkoutUrl;
        return;
      }
      message.error(getErrorMessage(error));
    }

    if (user) {
      const firstWorkspace = withoutOverview([...getRoleWorkspaces(user.role)])[0] || "profile";
      navigate(`/${firstWorkspace}`, { replace: true });
    } else {
      setAuthUser(null);
    }
  }

  async function completePaidStudentSignUp(reference: string) {
    const user = await runAction(
      async () => {
        const response = await confirmStudentSignupPayment(reference);
        if (!response.token) {
          clearAuthToken();
          message.info(response.message || "Payment confirmed. Please verify your email before signing in.");
          return null;
        }

        setAuthUser(response.user);
        if (response.user.role === "student") {
          await loadStudentBootstrap(response.user, false);
        } else {
          await loadBootstrap(undefined, false, response.user);
        }
        return response.user;
      },
      { successMessage: "Payment confirmed. Check your email to verify your account." }
    );

    if (user) {
      const firstWorkspace = withoutOverview([...getRoleWorkspaces(user.role)])[0] || "profile";
      navigate(`/${firstWorkspace}`, { replace: true });
    } else {
      setAuthUser(null);
      navigate("/login", { replace: true });
    }
  }

  async function handleStudentSignUp(values: {
    fullName: string;
    email: string;
    password: string;
    schoolName: string;
  }) {
    await runAction(
      async () => {
        const checkout = await registerStudent(values);
        if (!checkout.checkoutUrl) {
          throw new Error("Paystack payment could not be started.");
        }
        window.location.href = checkout.checkoutUrl;
        return true;
      },
      { successMessage: "Redirecting to payment..." }
    );
  }

  async function handleGoogleStudentSignUp(values: { credential: string; schoolName: string }) {
    await runAction(
      async () => {
        const checkout = await registerStudentWithGoogle(values);
        if (!checkout.checkoutUrl) {
          throw new Error("Paystack payment could not be started.");
        }
        window.location.href = checkout.checkoutUrl;
        return true;
      },
      { successMessage: "Redirecting to payment..." }
    );
  }

  async function handleLogout() {
    await logoutSession();
    setAuthUser(null);
    setAccounts([]);
    setSchools([]);
    setNotebooks([]);
    setSubjects([]);
    setTopics([]);
    setLessons([]);
    setMaterials([]);
    setAuditEvents([]);
    setBootstrapError(null);
    setBootstrapping(false);
    navigate("/login", { replace: true });
  }

  async function handleUpdateProfile(values: {
    fullName: string;
    organizationName: string;
  }) {
    const user = await runAction(
      async () => {
        const nextUser = await updateMyProfile(values);
        setAuthUser(nextUser);
        await refreshBootstrap();
        return nextUser;
      },
      { successMessage: "Profile updated." }
    );

    return user;
  }

  async function handleUpdateEmail(values: { email: string }) {
    const user = await runAction(
      async () => {
        const nextUser = await updateMyEmail(values.email);
        setAuthUser(nextUser);
        await refreshBootstrap();
        return nextUser;
      },
      { successMessage: "Email updated." }
    );

    return user;
  }

  async function handleRequestPasswordReset() {
    const result = await runAction(
      async () => {
        await requestMyPasswordReset();
        return true;
      },
      { successMessage: "Password reset link sent to your email." }
    );

    return result;
  }

  async function handleRequestPasswordResetByEmail(values: { email: string }) {
    return runAction(
      async () => {
        await requestPasswordReset(values.email);
        return true;
      },
      { successMessage: "If an account exists for that email, a reset link has been sent." }
    );
  }

  async function handleConfirmPasswordReset(values: { token: string; newPassword: string }) {
    await runAction(
      async () => {
        await confirmPasswordReset(values.token, values.newPassword);
        return true;
      },
      { successMessage: "Password reset complete. Please log in again." }
    );
    await handleLogout();
  }

  async function handleAcceptLegal(values: {
    privacyVersion: string;
    termsVersion: string;
    minorsConsentVersion?: string;
  }) {
    return runAction(
      async () => {
        await acceptMyConsent(values);
        return true;
      },
      { successMessage: "Legal consent saved." }
    );
  }

  async function handleDeleteMyData() {
    const result = await runAction(
      async () => {
        await requestMyDataDeletion();
        return true;
      },
      { successMessage: "Your account data has been deleted." }
    );
    if (result) {
      await handleLogout();
    }
    return result;
  }

  async function handleUpdateStudentSubjects(subjectIds: string[]) {
    if (!currentUser || currentUser.role !== "student") {
      return;
    }

    await runAction(
      async () => {
        const profile = await updateStudentSubjects(currentUser.id, subjectIds);
        const enrolled = profile.enrolledSubjectIds || [];
        setStudentEnrolledSubjectIds(enrolled);
        setAuthUser((user) => (user ? { ...user, enrolledSubjectIds: enrolled } : user));
        setSelectedSubjectId(enrolled[0] || "");
        await loadStudentBootstrap({ ...currentUser, enrolledSubjectIds: enrolled }, false);
        return profile;
      },
      { successMessage: "Subjects updated successfully.", errorMessage: "Failed to update subjects." }
    );
  }

  async function handleQuizAnswerChange(questionId: string, value: string) {
    setQuizAnswers((prev) => ({ ...prev, [questionId]: value }));
  }

  const sseCleanupRef = useRef<(() => void) | null>(null);

  async function handleGenerateQuiz(
    topicId?: string | string[],
    subjectIdOverride?: string,
    options?: { maxQuestions?: number; diagnosticLabel?: string }
  ) {
    const effectiveSubjectId = subjectIdOverride || selectedSubjectId;
    if (!currentUser || currentUser.role !== "student" || !effectiveSubjectId) {
      message.warning("Select an enrolled subject before starting a quiz.");
      return;
    }

    // Close any previous SSE connection
    if (sseCleanupRef.current) {
      sseCleanupRef.current();
      sseCleanupRef.current = null;
    }

    const subject = effectiveSubjects.find((s) => s.id === effectiveSubjectId);
    if (!subject) {
      message.warning("Select a subject to generate a quiz.");
      return;
    }

    let notebookId = notebookIds[effectiveSubjectId];
    if (!notebookId) {
      const id = await fetchStudentNotebook(currentUser.id, effectiveSubjectId);
      if (id) {
        notebookId = id;
        setNotebookIds((prev) => ({ ...prev, [effectiveSubjectId]: id }));
      }
    }

    setQuizGenerating(true);
    setSessionSummary(null);
    try {
      const selectedTopicIds = Array.isArray(topicId) ? topicId : topicId ? [topicId] : undefined;
      const result = await startQuizGeneration({
        studentId: currentUser.id,
        subjectId: effectiveSubjectId,
        maxQuestions: options?.maxQuestions || 6,
        topicId: selectedTopicIds?.[0],
        topicIds: selectedTopicIds,
        focusName: options?.diagnosticLabel,
      });

      if (result.status === 'pending') {
        message.info('Generating your quiz with AI... this may take up to a minute.', 5);
      }

      sseCleanupRef.current = watchQuizViaSSE(
        currentUser.id,
        effectiveSubjectId,
        (questions, quizId) => {
          sseCleanupRef.current = null;
          setCurrentQuizId(quizId ?? null);
          setQuizQuestions(questions);
          setQuizAnswers({});
          setQuizSessionActive(true);
          setQuizGenerating(false);
          if (activeWorkspaceRef.current !== "practice") {
            setAssessmentNotification({
              unread: true,
              questionCount: questions.length,
              topicName: selectedTopicIds?.length === 1 ? topics.find((t) => t.id === selectedTopicIds[0])?.name : undefined,
            });
          }
          const len = questions.length;
          if (selectedTopicIds?.length) {
            const topicNames = selectedTopicIds
              .map((id) => topics.find((t) => t.id === id)?.name)
              .filter(Boolean);
            const topicName = options?.diagnosticLabel || (topicNames.length > 1 ? `${topicNames.length} selected topics` : topicNames[0]) || "selected topic";
            message.success(`Generated ${len} diagnostic question${len === 1 ? "" : "s"} focused on ${topicName}.`);
          } else {
            message.success(`Generated ${len} question${len === 1 ? "" : "s"} based on your understanding and gaps.`);
          }
        },
        (error) => {
          sseCleanupRef.current = null;
          setCurrentQuizId(null);
          setQuizGenerating(false);
          message.error(`Quiz generation ${error ? 'failed: ' + error : 'connection lost'}`);
        },
        180000,
      );
    } catch (error) {
      setQuizGenerating(false);
      const errMsg = getErrorMessage(error);
      if (errMsg.includes('409') || errMsg.includes('CONFLICT') || errMsg.includes('already have a quiz')) {
        message.warning('You already have a quiz being generated. Please wait for it to complete.');
      } else {
        message.error(`Failed to generate quiz. ${errMsg}`);
      }
    }
  }

  async function handleGenerateTopicRecoveryQuiz(subjectId: string, topicId: string) {
    setSelectedSubjectId(subjectId);
    navigate("/practice");
    setActiveWorkspace("practice");
    await handleGenerateQuiz(topicId, subjectId);
  }

  async function handleGenerateDiagnosticPractice(subjectId: string, topicId: string, label?: string) {
    setSelectedSubjectId(subjectId);
    navigate("/practice");
    setActiveWorkspace("practice");
    await handleGenerateQuiz(topicId, subjectId, { maxQuestions: 5, diagnosticLabel: label });
  }

  async function handleResumeQuiz(quizId: string) {
    if (!currentUser) return;
    try {
      const quiz = await fetchQuizById(currentUser.id, quizId);
      if (!quiz || !Array.isArray(quiz.questions) || quiz.questions.length === 0) {
        message.warning('No saved questions found for this quiz. It may have been cleared.');
        return;
      }
      setCurrentQuizId(quizId);
      setQuizQuestions(quiz.questions);
      setQuizAnswers(quiz.answers || {});
      setQuizSessionActive(true);
      setSessionSummary(null);
    } catch {
      message.error('Failed to load quiz questions.');
    }
  }

  function handleExitQuizSession() {
    setQuizSessionActive(false);
    setCurrentQuizId(null);
    setQuizQuestions([]);
    setQuizAnswers({});
  }

  async function buildEndOfSessionSummary(
    subjectId: string,
    questions: QuizQuestion[],
    answers: Record<string, string>
  ): Promise<EndOfSessionSummary> {
    const strengthenedTopics = new Set<string>();
    const needsAttentionTopics = new Set<string>();
    let score = 0;

    for (const question of questions) {
      const selected = answers[question.id];
      const correct = selected === question.correctAnswer
        || (['A','B','C','D'].includes(question.correctAnswer) && selected === question.choices[['A','B','C','D'].indexOf(question.correctAnswer)]);
      const topicName = topics.find((topic) => topic.id === (question.topicId || question.prerequisiteTopicIds[0]))?.name
        || question.conceptTag
        || "this topic";

      if (correct) {
        score += 1;
        strengthenedTopics.add(topicName);
      } else {
        needsAttentionTopics.add(topicName);
      }
    }

    const nextStep = currentUser?.role === "student"
      ? await fetchRecommendedNextStep(currentUser.id, subjectId).catch(() => null)
      : null;

    if (nextStep) {
      setRecommendedNextStep(nextStep);
    }

    return {
      subjectId,
      score,
      total: questions.length,
      strengthenedTopics: Array.from(strengthenedTopics).slice(0, 3),
      needsAttentionTopics: Array.from(needsAttentionTopics).slice(0, 3),
      nextStepTitle: nextStep?.title,
      nextStepReason: nextStep?.reason,
    };
  }

  async function handleSubmitQuiz() {
    if (!currentUser || currentUser.role !== "student") {
      return;
    }

    setQuizSubmitting(true);
    try {
      if (intelligence) {
        let acc = intelligence;
        for (const question of quizQuestions) {
          const studentAnswer = quizAnswers[question.id];
          const correct = studentAnswer === question.correctAnswer
            || (['A','B','C','D'].includes(question.correctAnswer) && studentAnswer === question.choices[['A','B','C','D'].indexOf(question.correctAnswer)]);
          const result = processQuizAnswer(acc, question, correct, topics);
          acc = {
            ...result.updatedState,
            masteryState: result.updatedMastery,
            gapSignals: [...acc.gapSignals, ...result.gapSignals],
            lastUpdated: new Date().toISOString()
          };
          const topicLabel =
            topics.find(
              (t) =>
                t.id === (question.topicId || question.prerequisiteTopicIds[0])
            )?.name || "topic";
          if (!correct) {
            message.warning(`Gap detected in ${topicLabel}`);
          }
        }
        setIntelligence(acc);
        const updatedUnderstanding = await submitQuizAnswers(
          currentUser.id,
          quizQuestions,
          quizAnswers,
          currentQuizId ?? undefined
        );
        setUnderstanding(updatedUnderstanding);
        setSessionSummary(await buildEndOfSessionSummary(selectedSubjectId, quizQuestions, quizAnswers));
        setCurrentQuizId(null);
        setQuizAnswers({});
        setQuizSessionActive(false);
        message.success("Quiz submitted! Your understanding gaps have been updated.");
      } else {
        const updatedUnderstanding = await submitQuizAnswers(
          currentUser.id,
          quizQuestions,
          quizAnswers,
          currentQuizId ?? undefined
        );
        setUnderstanding(updatedUnderstanding);
        // Reconstruct client-side intelligence from server response
        {
          const topicMastery: Record<string, BKTParams> = {};
          const seenTopics = new Set<string>();
          if (updatedUnderstanding?.conceptMastery) {
            for (const cm of updatedUnderstanding.conceptMastery) {
              const tid = cm.topicId || cm.conceptId;
              if (tid) {
                topicMastery[tid] = {
                  topicId: tid,
                  pL0: cm.confidence,
                  pT: 0.1,
                  pS: 0.1,
                  pG: 0.25,
                  pKnown: cm.confidence,
                  attempts: 1,
                  lastUpdated: new Date().toISOString()
                };
                seenTopics.add(tid);
              }
            }
          }
          for (const topic of topics) {
            if (!seenTopics.has(topic.id)) {
              topicMastery[topic.id] = initializeBKTForTopic(topic.id);
            }
          }
          setIntelligence({
            masteryState: {
              studentId: currentUser.id,
              topicMastery,
              lastUpdated: new Date().toISOString()
            },
            gapSignals: [],
            socraticHistory: [],
            assessments: new Map(),
            calibratedQuestions: new Map(),
            lastUpdated: new Date().toISOString()
          });
        }
        setSessionSummary(await buildEndOfSessionSummary(selectedSubjectId, quizQuestions, quizAnswers));
        setCurrentQuizId(null);
        setQuizAnswers({});
        setQuizSessionActive(false);
        message.success("Quiz submitted! Your understanding gaps have been updated.");
      }
      const freshHistory = await fetchQuizHistory(currentUser.id);
      setQuizHistory(freshHistory);
    } finally {
      setQuizSubmitting(false);
    }
  }

  async function refreshChatSessionsForSubject(subjectId: string) {
    if (!currentUser || currentUser.role !== "student") return;
    const sessions = await fetchStudentChatSessions(currentUser.id, subjectId);
    setChatSessions(sessions);
  }

  async function handleCreateChatSession(subjectIdOverride?: string) {
    if (!currentUser || currentUser.role !== "student") return;
    const effectiveSubjectId = subjectIdOverride || selectedSubjectId;
    const session = await createStudentChatSession(currentUser.id, effectiveSubjectId);
    setChatSessions((prev) => [session, ...prev]);
    setActiveChatSessionId(session.id);
    setChatHistory([]);
  }

  async function handleSelectChatSession(chatSessionId: string) {
    if (chatSessionId !== activeChatSessionId) {
      setChatHistory([]);
    }
    setActiveChatSessionId(chatSessionId);
  }

  async function handleRenameChatSession(chatSessionId: string, title: string, subjectIdOverride?: string) {
    if (!currentUser || currentUser.role !== "student") return;
    const effectiveSubjectId = subjectIdOverride || selectedSubjectId;
    const updated = await updateStudentChatSessionTitle(currentUser.id, effectiveSubjectId, chatSessionId, title);
    setChatSessions((prev) => prev.map((session) => (session.id === updated.id ? updated : session)));
  }

  async function handleDeleteChatSession(chatSessionId: string, subjectIdOverride?: string) {
    if (!currentUser || currentUser.role !== "student") return;
    const effectiveSubjectId = subjectIdOverride || selectedSubjectId;
    await deleteStudentChatSession(currentUser.id, effectiveSubjectId, chatSessionId);

    let nextActiveSessionId: string | null = null;
    setChatSessions((prev) => {
      const remaining = prev.filter((session) => session.id !== chatSessionId);
      nextActiveSessionId = remaining[0]?.id || null;
      return remaining;
    });

    if (activeChatSessionId === chatSessionId) {
      setActiveChatSessionId(nextActiveSessionId);
      setChatHistory([]);
      if (!nextActiveSessionId) {
        await handleCreateChatSession(effectiveSubjectId);
      }
    }
  }

  async function handleSendChat(chatMessage: string, subjectIdOverride?: string, chatSessionIdOverride?: string) {
    console.log('[Chat] handleSendChat called with:', chatMessage);
    if (!currentUser || currentUser.role !== "student") {
      console.log('[Chat] Not a student, returning');
      return;
    }

    const effectiveSubjectId = subjectIdOverride || selectedSubjectId;
    if (!effectiveSubjectId || !effectiveSubjects.some((subject) => subject.id === effectiveSubjectId)) {
      message.warning("Select an enrolled subject before starting a chat.");
      return;
    }

    const effectiveChatSessionId = chatSessionIdOverride || activeChatSessionId || undefined;
    const requestId = Date.now();
    const pendingAssistantId = `pending-assistant-${requestId}`;
    const userMessage: ChatMessage = {
      id: `user-${requestId}`,
      role: "user",
      content: chatMessage,
      createdAt: new Date().toISOString()
    };
    const pendingAssistantMessage: ChatMessage = {
      id: pendingAssistantId,
      role: "assistant",
      content: "Thinking...",
      createdAt: new Date().toISOString()
    };

    setChatHistory((prev) => [...prev, userMessage, pendingAssistantMessage]);

    console.log('[Chat] Setting submitting true');
    setChatSubmitting(true);
    try {
      console.log('[Chat] Calling sendChatMessage with subject:', effectiveSubjectId);
      const response = await sendChatMessage(currentUser.id, effectiveSubjectId, chatMessage, false, understanding, effectiveChatSessionId);
      console.log('[Chat] Got response, assistant content:', response.assistant?.content?.substring(0, 100));
      if (response.chatSession) {
        setActiveChatSessionId(response.chatSession.id);
        setChatSessions((prev) => {
          const remaining = prev.filter((session) => session.id !== response.chatSession?.id);
          return [response.chatSession as StudentChatSession, ...remaining];
        });
      } else {
        await refreshChatSessionsForSubject(effectiveSubjectId).catch(() => {});
      }
      setChatHistory((prev) =>
        prev.map((message) => (message.id === pendingAssistantId ? response.assistant : message))
      );
      setUnderstanding(response.updatedProfile);
      if (response.recommendedNextStep) {
        setRecommendedNextStep(response.recommendedNextStep);
      }

      if (intelligence && selectedTopic) {
        const result = await processStudentInput(intelligence, chatMessage, topics, selectedTopic.id);
        setIntelligence((prev) =>
          prev
            ? {
                ...prev,
                masteryState: result.updatedMastery,
                gapSignals: [...prev.gapSignals, ...result.gapSignals],
                lastUpdated: new Date().toISOString()
              }
            : prev
        );
        if (result.gapSignals.length > 0) {
          message.info(`Detected ${result.gapSignals.length} understanding gap(s) from your question.`);
        }
        if (result.socraticPrompt) {
          const socraticMessage: ChatMessage = {
            id: `socratic-${Date.now()}`,
            role: "assistant",
            content: result.socraticPrompt,
            createdAt: new Date().toISOString()
          };
          setChatHistory((prev) => [...prev, socraticMessage]);
          message.info("Socratic guidance available - let's explore your understanding.");
        }
      }
    } catch (err) {
      console.error('[Chat] Error in handleSendChat:', err);
      setChatHistory((prev) => prev.filter((message) => message.id !== pendingAssistantId));
      message.error(getErrorMessage(err) || 'Failed to send chat message');
    } finally {
      setChatSubmitting(false);
    }
  }

  async function handlePublishLesson(
    publicationStatus: "draft" | "published",
    publishingNotes: string
  ) {
    if (!selectedLesson) {
      return;
    }

    await runAction(
      async () => {
        await setLessonPublication(selectedLesson.id, publicationStatus, publishingNotes);
        await refreshBootstrap();
      },
      {
        successMessage:
          publicationStatus === "published" ? "Lesson published." : "Lesson moved back to draft."
      }
    );
  }

  if (!authReady) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
        <Space direction="vertical" size={16} align="center">
          <Spin size="large" />
          <Text>Checking session...</Text>
        </Space>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <ConfigProvider theme={appTheme}>
        <AntApp>
          <Routes>
            <Route path="/login" element={
              <LoginWorkspace onLogin={handleLogin} onGoogleLogin={handleGoogleLogin} onShowSignUp={() => navigate("/signup")} onRequestPasswordReset={handleRequestPasswordResetByEmail} onConfirmPasswordReset={handleConfirmPasswordReset} />
            } />
            <Route path="/signup" element={
              <StudentSignUpWorkspace schools={schools} onSignUp={handleStudentSignUp} onGoogleSignUp={handleGoogleStudentSignUp} onBackToLogin={() => navigate("/login")} />
            } />
            <Route path="/reset-password" element={
              <PasswordResetWorkspace onRequestPasswordReset={handleRequestPasswordResetByEmail} onConfirmPasswordReset={handleConfirmPasswordReset} onBackToLogin={() => navigate("/login")} />
            } />
            <Route path="/*" element={<Navigate to="/login" replace />} />
          </Routes>
        </AntApp>
      </ConfigProvider>
    );
  }

  if (bootstrapping) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
        <Space direction="vertical" size={16} align="center">
          <Spin size="large" />
          <Text>Loading classroom workspace...</Text>
        </Space>
      </div>
    );
  }

  if (bootstrapError) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
        <Result
          status="error"
          title="Unable to load the app"
          subTitle={bootstrapError}
          extra={
            <Button type="primary" onClick={() => void loadBootstrap()}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  const user = currentUser;

  return (
    <ConfigProvider theme={appTheme}>
      <AntApp>
        <Routes>
          <Route path="/login" element={
            <LoginWorkspace onLogin={handleLogin} onGoogleLogin={handleGoogleLogin} onShowSignUp={() => navigate("/signup")} onRequestPasswordReset={handleRequestPasswordResetByEmail} onConfirmPasswordReset={handleConfirmPasswordReset} />
          } />
          <Route path="/signup" element={
            <StudentSignUpWorkspace schools={schools} onSignUp={handleStudentSignUp} onGoogleSignUp={handleGoogleStudentSignUp} onBackToLogin={() => navigate("/login")} />
          } />
          <Route path="/reset-password" element={
            <PasswordResetWorkspace onRequestPasswordReset={handleRequestPasswordResetByEmail} onConfirmPasswordReset={handleConfirmPasswordReset} onBackToLogin={() => navigate("/login")} />
          } />
          <Route path="/*" element={
            <ProtectedRoute user={user}>
              <Layout style={{ minHeight: "100vh" }}>
                <Sider width={260} breakpoint="lg" collapsedWidth="0">
                  <div style={{ padding: 24 }}>
                    <Title level={3} style={{ color: "#fff", margin: 0 }}>
                      Kwazi
                    </Title>
                  </div>
                  <Menu
                    theme="dark"
                    mode="inline"
                    selectedKeys={[activeWorkspace]}
                    items={menuItems}
                    onClick={({ key }) => {
                      navigate(`/${key}`);
                      setActiveWorkspace(key as WorkspaceKey);
                    }}
                  />
                </Sider>
                <Layout>
                  <Header style={{ padding: "0 24px" }}>
                    <Row gutter={[16, 0]} align="middle" justify="space-between">
                      <Col flex="auto">
                        {!["profile", "understanding-map", "practice", "chat", "materials"].includes(activeWorkspace) ? (
                          <Space direction="vertical" size={2}>
                            <Text type="secondary">Workspace</Text>
                            <Title level={4} style={{ margin: 0 }}>
                              {workspaceLabels[activeWorkspace]}
                            </Title>
                          </Space>
                        ) : (
                          <div style={{ height: 46 }} />
                        )}
                      </Col>
                      <Col flex="none" style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Space size={16} align="center">
                          {currentUser?.role === "student" ? (
                            <Badge
                              dot={hasUnreadAssessment}
                              count={hasUnreadAssessment ? 1 : 0}
                              size="small"
                              style={{ backgroundColor: brandTokens.colorWarning }}
                            >
                              <Button
                                aria-label={hasUnreadAssessment ? "Assessment ready" : "No assessment notifications"}
                                type="text"
                                icon={<BellOutlined />}
                                onClick={() => {
                                  setAssessmentNotification((current) => current ? { ...current, unread: false } : current);
                                  navigate("/practice");
                                  setActiveWorkspace("practice");
                                }}
                                style={{
                                  border: `1px solid ${hasUnreadAssessment ? brandTokens.colorWarning : brandTokens.colorBorder}`,
                                  background: hasUnreadAssessment ? "rgba(250, 173, 20, 0.12)" : brandTokens.colorSurface,
                                  color: hasUnreadAssessment ? brandTokens.colorWarning : brandTokens.colorInk,
                                }}
                              />
                            </Badge>
                          ) : null}
                          <Dropdown
                            trigger={["click"]}
                            menu={{
                              items: accountMenuItems,
                              onClick: ({ key }) => {
                                if (key === "assessment-ready") {
                                  setAssessmentNotification((current) => current ? { ...current, unread: false } : current);
                                  navigate("/practice");
                                  setActiveWorkspace("practice");
                                  return;
                                }
                                if (key === "profile") {
                                  navigate("/profile");
                                  setActiveWorkspace("profile");
                                  return;
                                }
                                if (key === "logout") {
                                  void handleLogout();
                                }
                              }
                            }}
                          >
                            <Button
                              type="text"
                              style={{
                                height: 40,
                                padding: "6px 10px",
                                margin: 0,
                                borderRadius: brandTokens.radiusMd,
                                border: `1px solid ${brandTokens.colorBorder}`,
                                background: brandTokens.colorSurface,
                                display: "inline-flex",
                                alignItems: "center",
                                transform: "translateY(-4px)"
                              }}
                            >
                              <Space size={8} align="center">
                                <Badge dot={hasUnreadAssessment} offset={[-2, 4]}>
                                  <Avatar size={24} icon={<UserOutlined />} />
                                </Badge>
                                {hasUnreadAssessment ? (
                                  <Badge
                                    count={1}
                                    size="small"
                                    style={{ backgroundColor: brandTokens.colorWarning }}
                                  >
                                    <BellOutlined style={{ color: brandTokens.colorInk, fontSize: 16 }} />
                                  </Badge>
                                ) : null}
                                <span style={{ color: brandTokens.colorHeading, fontWeight: 600 }}>
                                  {currentUser?.fullName || "Loading user"}
                                </span>
                                <DownOutlined style={{ color: brandTokens.colorInk, fontSize: 10 }} />
                              </Space>
                            </Button>
                          </Dropdown>
                        </Space>
                      </Col>
                    </Row>
                  </Header>
                  <Content style={{ padding: 24 }}>
                    <Space direction="vertical" size={16} style={{ display: "flex" }}>
                      {currentUser?.role === "superadmin" && (
                        <Alert
                          type="success"
                          showIcon
                          message="Superadmin workspace"
                          description="This production shell is locked to the authenticated superadmin journey: schools, accounts, materials, topic prerequisites, and lesson publishing."
                        />
                      )}

                      <HeroCards
                        account={currentUser}
                        schools={schools}
                        lessons={visibleLessons}
                        topics={topics.filter((topic) => topic.subjectId === effectiveSubjects.find(s => s.id === selectedSubjectId)?.id)}
                        visible={currentUser?.role !== "student"}
                      />

                      {activeWorkspace === "materials" ? (
                        <MaterialsWorkspace
                          materials={currentUser?.role === "superadmin" || currentUser?.role === "student" ? materials : selectedMaterials}
                          topics={topics}
                          subjects={effectiveSubjects}
                          schools={schools}
                          onCreateMaterial={handleCreateMaterial}
                          onUpdateMaterial={handleUpdateMaterial}
                          onDeleteMaterial={handleDeleteMaterial}
                          onBulkDeleteMaterial={handleBulkDeleteMaterial}
                          superadminMode={currentUser?.role === "superadmin"}
                          refreshBootstrap={refreshBootstrap}
                          currentUser={currentUser}
                        />
                      ) : null}

                      {activeWorkspace === "topic-graph" ? (
                        <TopicGraphEditor
                          topics={topics}
                          subjects={subjects}
                          onCreateTopic={handleCreateTopic}
                          onUpdateTopic={handleSaveTopic}
                          superadminMode={currentUser?.role === "superadmin"}
                        />
                      ) : null}

                      {activeWorkspace === "schools" ? (
                        <SchoolsWorkspace
                          schools={schools}
                          canManage={currentUser?.role === "superadmin"}
                          modalOpen={schoolModalOpen}
                          onOpenModal={() => setSchoolModalOpen(true)}
                          onCloseModal={() => setSchoolModalOpen(false)}
                          onCreateSchool={handleCreateSchool}
                          onDeleteSchool={handleDeleteSchool}
                        />
                      ) : null}

                      {activeWorkspace === "subjects" ? (
                        <SubjectsWorkspace
                          subjects={subjects}
                          topics={topics}
                          subjectTopicsCache={subjectTopicsCache}
                          setSubjectTopicsCache={setSubjectTopicsCache}
                          expandedRowKeys={expandedRowKeys}
                          setExpandedRowKeys={setExpandedRowKeys}
                          onCreateSubject={handleCreateSubject}
                          onUpdateSubject={handleUpdateSubject}
                          onDeleteSubject={handleDeleteSubject}
                          canManage={currentUser?.role === "superadmin"}
                        />
                      ) : null}

                      {activeWorkspace === "accounts" ? (
                        <AccountsWorkspace
                          accounts={accounts}
                          schools={schools}
                          onAssignRole={handleAssignRole}
                          modalOpen={accountModalOpen}
                          onOpenModal={() => setAccountModalOpen(true)}
                          onCloseModal={() => setAccountModalOpen(false)}
                          onCreateAccount={handleCreateAccount}
                        />
                      ) : null}

                      {activeWorkspace === "api-status" ? (
                        <ApiStatusWorkspace apiHealth={apiHealth} />
                      ) : null}

                      {activeWorkspace === "profile" ? (
                        <ProfileWorkspace
                          currentUser={currentUser}
                          schools={schools}
                          subjects={subjects}
                          enrolledSubjectIds={studentEnrolledSubjectIds}
                          onUpdateProfile={handleUpdateProfile}
                          onUpdateEmail={handleUpdateEmail}
                          onUpdateSubjects={handleUpdateStudentSubjects}
                          onRequestPasswordReset={handleRequestPasswordReset}
                          onConfirmPasswordReset={handleConfirmPasswordReset}
                          onFetchLegalDocuments={fetchLegalDocuments}
                          onFetchConsent={fetchMyConsent}
                          onAcceptLegal={handleAcceptLegal}
                          onDeleteMyData={handleDeleteMyData}
                        />
                      ) : null}

                      {activeWorkspace === "practice" ? (
                        <PracticeWorkspace
                          questions={quizQuestions}
                          answers={quizAnswers}
                          submitting={quizSubmitting}
                          generating={quizGenerating}
                          quizSessionActive={quizSessionActive}
                          subjects={effectiveSubjects}
                          currentUser={currentUser}
                          intelligence={intelligence}
                          understanding={understanding}
                          sessionSummary={sessionSummary}
                          allTopics={topics}
                          syncSelectedSubjectId={selectedSubjectId}
                          onAnswerChange={handleQuizAnswerChange}
                          onSubmitQuiz={handleSubmitQuiz}
                          onGenerateQuiz={handleGenerateQuiz}
                          onResumeQuiz={handleResumeQuiz}
                          onExitQuiz={handleExitQuizSession}
                          onSubjectChange={setSelectedSubjectId}
                          quizHistory={quizHistory}
                        />
                      ) : null}

                      {activeWorkspace === "understanding-map" ? (
                        <UnderstandingMapWorkspace
                          topics={topics}
                          subjects={effectiveSubjects}
                          intelligence={intelligence}
                          currentUser={currentUser}
                          recommendedNextStep={recommendedNextStep}
                          recommendedNextStepLoading={recommendedNextStepLoading}
                          syncSelectedSubjectId={selectedSubjectId}
                          onSubjectChange={setSelectedSubjectId}
                          onGenerateTopicQuiz={handleGenerateTopicRecoveryQuiz}
                          onGenerateDiagnosticPractice={handleGenerateDiagnosticPractice}
                        />
                      ) : null}

                      {activeWorkspace === "chat" ? (
                        <ChatWorkspace
                          chatHistory={chatHistory}
                          chatSessions={chatSessions}
                          activeChatSessionId={activeChatSessionId}
                          chatSessionsLoading={chatSessionsLoading}
                          submitting={chatSubmitting}
                          subjects={effectiveSubjects}
                          currentUser={currentUser}
                          syncSelectedSubjectId={selectedSubjectId}
                          onSendChat={handleSendChat}
                          onCreateChatSession={handleCreateChatSession}
                          onSelectChatSession={handleSelectChatSession}
                          onRenameChatSession={handleRenameChatSession}
                          onDeleteChatSession={handleDeleteChatSession}
                          onSubjectChange={setSelectedSubjectId}
                        />
                      ) : null}

                    </Space>
                  </Content>
                </Layout>
              </Layout>
            </ProtectedRoute>
          } />
        </Routes>
      </AntApp>
    </ConfigProvider>
  );
}

function GoogleAuthButton({
  text,
  onCredential
}: {
  text: "signin_with" | "signup_with";
  onCredential: (credential: string) => Promise<void>;
}) {
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const onCredentialRef = useRef(onCredential);
  const [status, setStatus] = useState<"loading" | "ready" | "unavailable">("loading");

  useEffect(() => {
    onCredentialRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => {
    let cancelled = false;
    let googleReadyInterval: number | null = null;

    const loadGoogleButton = async () => {
      try {
        const config = await fetchGoogleAuthConfig();
        if (!config.clientId || cancelled) {
          setStatus("unavailable");
          return;
        }

        const renderGoogleButton = () => {
          if (cancelled || !window.google || !googleButtonRef.current) {
            return;
          }

          googleButtonRef.current.innerHTML = "";
          window.google.accounts.id.initialize({
            client_id: config.clientId,
            callback: async (response) => {
              if (!response.credential) {
                message.error("Google did not return an auth credential.");
                return;
              }

              await onCredentialRef.current(response.credential);
            }
          });
          window.google.accounts.id.renderButton(googleButtonRef.current, {
            theme: "outline",
            size: "large",
            width: "360",
            text
          });
          setStatus("ready");
        };

        const waitForGoogle = () => {
          const startedAt = Date.now();
          googleReadyInterval = window.setInterval(() => {
            if (window.google && googleButtonRef.current) {
              if (googleReadyInterval) window.clearInterval(googleReadyInterval);
              googleReadyInterval = null;
              renderGoogleButton();
              return;
            }

            if (Date.now() - startedAt > 5000) {
              if (googleReadyInterval) window.clearInterval(googleReadyInterval);
              googleReadyInterval = null;
              if (!cancelled) setStatus("unavailable");
            }
          }, 50);
        };

        if (window.google) {
          if (googleButtonRef.current) {
            renderGoogleButton();
          } else {
            waitForGoogle();
          }
          return;
        }

        const existingScript = document.querySelector<HTMLScriptElement>('script[src="https://accounts.google.com/gsi/client"]');
        if (existingScript) {
          existingScript.addEventListener("load", renderGoogleButton, { once: true });
          waitForGoogle();
          return;
        }

        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = renderGoogleButton;
        script.onerror = () => setStatus("unavailable");
        document.head.appendChild(script);
        waitForGoogle();
      } catch {
        if (!cancelled) setStatus("unavailable");
      }
    };

    void loadGoogleButton();

    return () => {
      cancelled = true;
      if (googleReadyInterval) window.clearInterval(googleReadyInterval);
    };
  }, [text]);

  if (status === "unavailable") {
    return null;
  }

  return (
    <div style={{ display: "grid", placeItems: "center", marginTop: 16 }}>
      <div ref={googleButtonRef} />
      {status === "loading" ? (
        <Text type="secondary" style={{ marginTop: 8 }}>
          Loading Google auth...
        </Text>
      ) : null}
    </div>
  );
}

function LoginWorkspace({
  onLogin,
  onGoogleLogin,
  onShowSignUp,
  onRequestPasswordReset,
  onConfirmPasswordReset
}: {
  onLogin: (values: { email: string; password: string }) => Promise<void>;
  onGoogleLogin: (credential: string) => Promise<void>;
  onShowSignUp: () => void;
  onRequestPasswordReset: (values: { email: string }) => Promise<boolean | null>;
  onConfirmPasswordReset: (values: { token: string; newPassword: string }) => Promise<void>;
}) {
  const [form] = Form.useForm();
  const [resetForm] = Form.useForm();
  const [resetMode, setResetMode] = useState<"login" | "request" | "confirm">("login");

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
      <Card title="Login" style={{ width: "100%", maxWidth: 460 }}>
        {resetMode === "login" ? (
          <>
            <Form
              form={form}
              layout="vertical"
              onFinishFailed={({ errorFields }) => {
                if (errorFields.length > 0) {
                  message.error(errorFields[0]?.errors[0] || "Enter your email and password.");
                }
              }}
              onFinish={async (values) => {
                await onLogin(values);
              }}
            >
              <Form.Item
                name="email"
                label="Email"
                rules={[{ required: true }, { type: "email", message: "Enter a valid email." }]}
              >
                <Input autoComplete="email" />
              </Form.Item>
              <Form.Item name="password" label="Password" rules={[{ required: true }]}>
                <Input.Password autoComplete="current-password" />
              </Form.Item>
              <Button type="primary" htmlType="submit" block>
                Login
              </Button>
            </Form>
            <Button type="link" block style={{ marginTop: 8 }} onClick={() => setResetMode("request")}>
              Forgot your password?
            </Button>
            <Divider plain>OR</Divider>
            <GoogleAuthButton text="signin_with" onCredential={onGoogleLogin} />
            <div style={{ marginTop: 16, textAlign: "center" }}>
              <Text type="secondary">Don't have an account? </Text>
              <Button type="link" onClick={onShowSignUp}>
                Create account
              </Button>
            </div>
          </>
        ) : (
          <PasswordResetPanel
            form={resetForm}
            mode={resetMode}
            onModeChange={setResetMode}
            onRequestPasswordReset={onRequestPasswordReset}
            onConfirmPasswordReset={onConfirmPasswordReset}
          />
        )}
      </Card>
    </div>
  );
}

function PasswordResetWorkspace({
  onRequestPasswordReset,
  onConfirmPasswordReset,
  onBackToLogin
}: {
  onRequestPasswordReset: (values: { email: string }) => Promise<boolean | null>;
  onConfirmPasswordReset: (values: { token: string; newPassword: string }) => Promise<void>;
  onBackToLogin: () => void;
}) {
  const [form] = Form.useForm();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token") || "";
  const [mode, setMode] = useState<"request" | "confirm">(token ? "confirm" : "request");

  useEffect(() => {
    if (token) form.setFieldsValue({ token });
  }, [form, token]);

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
      <Card title="Reset password" style={{ width: "100%", maxWidth: 460 }} extra={<Button type="link" onClick={onBackToLogin}>Back to login</Button>}>
        <PasswordResetPanel
          form={form}
          mode={mode}
          onModeChange={(nextMode) => {
            if (nextMode === "login") onBackToLogin();
            else setMode(nextMode);
          }}
          onRequestPasswordReset={onRequestPasswordReset}
          onConfirmPasswordReset={onConfirmPasswordReset}
        />
      </Card>
    </div>
  );
}

function PasswordResetPanel({
  form,
  mode,
  onModeChange,
  onRequestPasswordReset,
  onConfirmPasswordReset
}: {
  form: ReturnType<typeof Form.useForm>[0];
  mode: "request" | "confirm";
  onModeChange: (mode: "login" | "request" | "confirm") => void;
  onRequestPasswordReset: (values: { email: string }) => Promise<boolean | null>;
  onConfirmPasswordReset: (values: { token: string; newPassword: string }) => Promise<void>;
}) {
  if (mode === "request") {
    return (
      <Form form={form} layout="vertical" onFinish={async (values) => { await onRequestPasswordReset(values); onModeChange("confirm"); }}>
        <Paragraph type="secondary">Enter your account email and we’ll send a reset link if the account exists.</Paragraph>
        <Form.Item name="email" label="Email" rules={[{ required: true }, { type: "email", message: "Enter a valid email." }]}>
          <Input autoComplete="email" />
        </Form.Item>
        <Button type="primary" htmlType="submit" block>Send reset link</Button>
        <Button type="link" block onClick={() => onModeChange("login")}>Back to login</Button>
      </Form>
    );
  }

  return (
    <Form form={form} layout="vertical" onFinish={async (values) => { await onConfirmPasswordReset(values); }}>
      <Paragraph type="secondary">Paste the reset token from your email, or use the reset link directly.</Paragraph>
      <Form.Item name="token" label="Reset token" rules={[{ required: true, message: "Enter the reset token." }]}>
        <Input autoComplete="one-time-code" />
      </Form.Item>
      <Form.Item name="newPassword" label="New password" rules={[{ required: true, message: "Enter a new password." }, { min: 8, message: "Use at least 8 characters." }]}>
        <Input.Password autoComplete="new-password" />
      </Form.Item>
      <Button type="primary" htmlType="submit" block>Set new password</Button>
      <Button type="link" block style={{ marginTop: 8 }} onClick={() => onModeChange("request")}>Request a new link</Button>
      <Button type="link" block onClick={() => onModeChange("login")}>Back to login</Button>
    </Form>
  );
}

function StudentSignUpWorkspace({
  schools,
  onSignUp,
  onGoogleSignUp,
  onBackToLogin
}: {
  schools: School[];
  onSignUp: (values: {
    fullName: string;
    email: string;
    password: string;
    schoolName: string;
  }) => Promise<void>;
  onGoogleSignUp: (values: { credential: string; schoolName: string }) => Promise<void>;
  onBackToLogin: () => void;
}) {
  const [form] = Form.useForm();
  const [membershipPrice, setMembershipPrice] = useState({ currency: "ZAR", amount: "60.00" });

  useEffect(() => {
    let cancelled = false;
    void fetchSignupPaymentConfig()
      .then((config) => {
        if (cancelled) return;
        setMembershipPrice({
          currency: (config.currency || "zar").toUpperCase(),
          amount: ((config.amountCents || 0) / 100).toFixed(2)
        });
      })
      .catch(() => {
        // Keep the default display price if the config endpoint is unavailable.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  async function handleGoogleCredential(credential: string) {
    try {
      const { schoolName } = await form.validateFields(["schoolName"]);
      await onGoogleSignUp({ credential, schoolName });
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      }
    }
  }

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
      <Card
        title="Sign up"
        style={{ width: "100%", maxWidth: 460 }}
        extra={<Button type="link" onClick={onBackToLogin}>Back to login</Button>}
      >
        <div style={{ marginBottom: 24 }}>
          <Text type="secondary" style={{ display: "block", marginBottom: 4, fontWeight: 700 }}>
            Membership price
          </Text>
          <div style={{ alignItems: "baseline", display: "flex", gap: 8 }}>
            <Text data-testid="membership-currency" style={{ fontSize: 16, fontWeight: 800, letterSpacing: 0.8 }}>
              {membershipPrice.currency}
            </Text>
            <Title data-testid="membership-amount" level={1} style={{ fontSize: 48, lineHeight: 1, margin: 0, fontWeight: 900 }}>
              {membershipPrice.amount}
            </Title>
          </div>
          <Text type="secondary">
            Your account is activated after successful payment via Paystack.
          </Text>
        </div>
        <Form
          form={form}
          layout="vertical"
          onFinishFailed={({ errorFields }) => {
            if (errorFields.length > 0) {
              message.error(errorFields[0]?.errors[0] || "Complete all required fields.");
            }
          }}
          onFinish={async (values) => {
            await onSignUp(values);
          }}
        >
          <Form.Item
            name="fullName"
            label="Full name"
            rules={[{ required: true, message: "Enter your full name." }]}
          >
            <Input autoComplete="name" placeholder="Enter your first and last name" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email address"
            rules={[
              { required: true, message: "Enter your email address." },
              { type: "email", message: "Enter a valid email address." }
            ]}
          >
            <Input
              autoComplete="email"
              placeholder="you@example.com"
              type="email"
            />
          </Form.Item>
          <Form.Item
            name="schoolName"
            label="School"
            rules={[{ required: true, message: "Enter your school name." }]}
          >
            <AutoComplete
              options={schools.map((school) => ({
                value: school.name
              }))}
              placeholder="Start typing your school name"
              filterOption={(input, option) =>
                (option?.value as string ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: "Create a password." },
              { min: 8, message: "Password must be at least 8 characters." }
            ]}
          >
            <Input.Password
              autoComplete="new-password"
              placeholder="At least 8 characters"
            />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm password"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Confirm your password." },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match."));
                }
              })
            ]}
          >
            <Input.Password autoComplete="new-password" placeholder="Re-enter your password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>
            Create account
          </Button>
        </Form>
        <Divider plain>OR</Divider>
        <GoogleAuthButton text="signup_with" onCredential={handleGoogleCredential} />
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <Text type="secondary">Already have an account? </Text>
          <Button type="link" onClick={onBackToLogin}>
            Sign in
          </Button>
        </div>
      </Card>
    </div>
  );
}

function ProfileWorkspace({
  currentUser,
  schools,
  subjects,
  enrolledSubjectIds,
  onUpdateProfile,
  onUpdateEmail,
  onUpdateSubjects,
  onRequestPasswordReset,
  onConfirmPasswordReset,
  onFetchLegalDocuments,
  onFetchConsent,
  onAcceptLegal,
  onDeleteMyData,
}: {
  currentUser: UserAccount;
  schools: School[];
  subjects: Subject[];
  enrolledSubjectIds: string[];
  onUpdateProfile: (values: {
    fullName: string;
    organizationName: string;
  }) => Promise<UserAccount | null>;
  onUpdateEmail: (values: { email: string }) => Promise<UserAccount | null>;
  onUpdateSubjects: (subjectIds: string[]) => Promise<void>;
  onRequestPasswordReset: () => Promise<boolean | null>;
  onConfirmPasswordReset: (values: {
    token: string;
    newPassword: string;
  }) => Promise<void>;
  onFetchLegalDocuments: () => Promise<Array<{ doc_type: "privacy" | "terms" | "minors-consent"; version: string; title: string; content: string }>>;
  onFetchConsent: () => Promise<{ privacy_version?: string; terms_version?: string; minors_consent_version?: string; accepted_at?: string } | null>;
  onAcceptLegal: (values: { privacyVersion: string; termsVersion: string; minorsConsentVersion?: string }) => Promise<boolean | null>;
  onDeleteMyData: () => Promise<boolean | null>;
}) {
  const [profileForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [resetForm] = Form.useForm();
  const [activeSection, setActiveSection] = useState<"identity" | "learning" | "security">("identity");
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>(enrolledSubjectIds);
  const [savingSubjects, setSavingSubjects] = useState(false);
  const [legalDocs, setLegalDocs] = useState<Array<{ doc_type: "privacy" | "terms" | "minors-consent"; version: string; title: string; content: string }>>([]);
  const [consent, setConsent] = useState<{ privacy_version?: string; terms_version?: string; minors_consent_version?: string; accepted_at?: string } | null>(null);

  useEffect(() => {
    onFetchLegalDocuments().then(setLegalDocs).catch(() => {});
    onFetchConsent().then(setConsent).catch(() => {});
  }, [onFetchConsent, onFetchLegalDocuments]);

  useEffect(() => {
    profileForm.setFieldsValue({
      fullName: currentUser.fullName,
      organizationName: currentUser.organizationName
    });
    emailForm.setFieldsValue({
      email: currentUser.email
    });
  }, [currentUser, emailForm, profileForm]);

  useEffect(() => {
    setSelectedSubjects(enrolledSubjectIds);
  }, [enrolledSubjectIds]);

  const handleSaveSubjects = async () => {
    setSavingSubjects(true);
    try {
      await onUpdateSubjects(selectedSubjects);
    } finally {
      setSavingSubjects(false);
    }
  };

  const hasExistingEnrolled = enrolledSubjectIds.length > 0;
  const hasChanges = JSON.stringify(selectedSubjects) !== JSON.stringify(enrolledSubjectIds);
  const canSave = hasChanges || !hasExistingEnrolled;
  const stepItems: Array<{ key: "identity" | "learning" | "security"; label: string; helper: string }> = [
    { key: "identity", label: "Identity", helper: "Name, school and role details" },
    { key: "learning", label: "Learning", helper: "Subjects and learning setup" },
    { key: "security", label: "Security", helper: "Email and password controls" }
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card>
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col xs={24} md={16}>
            <Space direction="vertical" size={4}>
              <Title level={3} style={{ margin: 0 }}>Profile Settings</Title>
              <Text type="secondary">Manage identity, enrollment, security, and account activity.</Text>
            </Space>
          </Col>
          <Col xs={24} md={8}>
            <Space direction="vertical" size={2} style={{ display: "flex", textAlign: "right" }}>
              <Text strong>{currentUser.fullName}</Text>
              <Text type="secondary">{currentUser.role}</Text>
              <Tag color="processing">{currentUser.organizationName}</Tag>
            </Space>
          </Col>
        </Row>
      </Card>

      {currentUser.role === "student" && !hasExistingEnrolled && (
        <Alert
          type="warning"
          showIcon
          message="No subjects enrolled"
          description="Please select at least one subject to get started with your lessons."
        />
      )}

      <Tabs
        activeKey={activeSection}
        onChange={(key) => setActiveSection(key as "identity" | "learning" | "security")}
        items={stepItems.map((item) => ({
          key: item.key,
          label: item.label,
        }))}
      />

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          {activeSection === "identity" && (
            <Card title="Identity Settings">
              <Form form={profileForm} layout="vertical" onFinish={(values) => void onUpdateProfile(values)}>
                <Row gutter={[12, 12]}>
                  <Col xs={24} md={12}>
                    <Form.Item name="fullName" label="Full name" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="organizationName" label="School" rules={[{ required: true }]}>
                      <Select
                        showSearch
                        options={schools.map((school) => ({
                          value: school.name,
                          label: `${school.name} (${school.shortCode})`
                        }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Button type="primary" htmlType="submit">
                  Save identity
                </Button>
              </Form>
            </Card>
          )}

          {activeSection === "learning" && (
            <Card title="Enrollment">
              {currentUser.role === "student" ? (
                <>
                  <Paragraph type="secondary">Select the subjects you're studying this year.</Paragraph>
                  <Select
                    mode="multiple"
                    style={{ width: "100%", marginBottom: 16 }}
                    placeholder={hasExistingEnrolled ? "" : "Choose subjects"}
                    value={selectedSubjects}
                    onChange={setSelectedSubjects}
                    options={subjects.map((subject) => ({
                      value: subject.id,
                      label: subject.name
                    }))}
                  />
                  <Button
                    type="primary"
                    loading={savingSubjects}
                    onClick={handleSaveSubjects}
                    disabled={!canSave}
                  >
                    {hasExistingEnrolled ? "Update subjects" : "Enroll in subjects"}
                  </Button>
                </>
              ) : (
                <Empty description="Enrollment management is available for student accounts." />
              )}
            </Card>
          )}

          {activeSection === "security" && (
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Card title="Email">
                <Form form={emailForm} layout="vertical" onFinish={(values) => void onUpdateEmail(values)}>
                  <Form.Item
                    name="email"
                    label="Email address"
                    rules={[{ required: true }, { type: "email", message: "Enter a valid email." }]}
                  >
                    <Input />
                  </Form.Item>
                  <Button type="primary" htmlType="submit">
                    Update email
                  </Button>
                </Form>
              </Card>

              <Card title="Password reset">
                <Paragraph type="secondary">
                  Request a password reset link and complete it from your inbox.
                </Paragraph>
                <Button onClick={() => void onRequestPasswordReset()}>
                  Request password reset
                </Button>
              </Card>

              <Card title="Privacy, Terms, and Consent">
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  {legalDocs.map((doc) => (
                    <Card key={`${doc.doc_type}-${doc.version}`} size="small" title={`${doc.title} (${doc.version})`}>
                      <Text type="secondary">{doc.content}</Text>
                    </Card>
                  ))}
                  {consent?.accepted_at ? (
                    <Alert
                      type="success"
                      showIcon
                      message="Consent recorded"
                      description={`Accepted on ${new Date(consent.accepted_at).toLocaleString()}`}
                    />
                  ) : (
                    <Alert
                      type="warning"
                      showIcon
                      message="Consent pending"
                      description="Please accept privacy and terms before pilot participation."
                    />
                  )}
                  <Button
                    type="primary"
                    onClick={async () => {
                      const privacy = legalDocs.find((doc) => doc.doc_type === "privacy")?.version || "v1";
                      const terms = legalDocs.find((doc) => doc.doc_type === "terms")?.version || "v1";
                      const minors = legalDocs.find((doc) => doc.doc_type === "minors-consent")?.version;
                      const ok = await onAcceptLegal({
                        privacyVersion: privacy,
                        termsVersion: terms,
                        minorsConsentVersion: minors,
                      });
                      if (ok) {
                        const nextConsent = await onFetchConsent();
                        setConsent(nextConsent);
                      }
                    }}
                  >
                    Accept current legal documents
                  </Button>
                </Space>
              </Card>

              <Card title="Data Deletion">
                <Paragraph type="secondary">
                  Delete your account data (chat history, gap signals, quizzes, mastery records, and profile).
                </Paragraph>
                <Button
                  danger
                  onClick={() => {
                    Modal.confirm({
                      title: "Delete your account data?",
                      content: "This action is permanent and signs you out.",
                      okText: "Delete permanently",
                      okButtonProps: { danger: true },
                      onOk: async () => {
                        await onDeleteMyData();
                      },
                    });
                  }}
                >
                  Delete my data
                </Button>
              </Card>
            </Space>
          )}
        </Col>
      </Row>

    </Space>
  );
}

function HeroCards({
  account,
  schools,
  lessons,
  topics,
  visible = true
}: {
  account?: UserAccount;
  schools: School[];
  lessons: Lesson[];
  topics: Topic[];
  visible?: boolean;
}) {
  if (!visible) {
    return null;
  }
  const draftLessons = lessons.filter((item) => item.publicationStatus !== "published").length;
  const blockedLessons = lessons.filter((item) => {
    const hasTranscript = Boolean(item.transcript?.trim());
    const hasSummary = Boolean(item.summary.highLevelSummary?.trim());
    const hasDependencies = (item.lessonDependencyIds?.length || 0) > 0;
    return item.publicationStatus !== "published" && (!hasTranscript || !hasSummary || !hasDependencies);
  }).length;
  const topicsMissingDependencies = topics.filter(
    (topic) => topic.prerequisiteTopicIds.length === 0
  ).length;

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={6}>
        <Card>
          <Statistic title="Schools" value={schools.length} suffix="managed" />
        </Card>
      </Col>
      <Col xs={24} md={6}>
        <Card>
          <Statistic title="Draft lessons" value={draftLessons} />
        </Card>
      </Col>
      <Col xs={24} md={6}>
        <Card>
          <Statistic title="Publishing blockers" value={blockedLessons} />
        </Card>
      </Col>
      <Col xs={24} md={6}>
        <Card>
          <Statistic title="Topics missing prereqs" value={topicsMissingDependencies} />
        </Card>
      </Col>
    </Row>
  );
}

function SuperadminOverviewWorkspace({
  schools,
  accounts,
  lessons,
  topics,
  subjects,
  materials,
  apiHealth,
  lesson,
  topic,
  governanceSummary,
  onPublish
}: {
  schools: School[];
  accounts: UserAccount[];
  lessons: Lesson[];
  topics: Topic[];
  subjects: Subject[];
  materials: StudyMaterial[];
  apiHealth: ApiHealth[];
  lesson?: Lesson;
  topic?: Topic;
  governanceSummary: GovernanceSummary | null;
  onPublish: (
    publicationStatus: "draft" | "published",
    publishingNotes: string
  ) => Promise<void>;
}) {
  const [notes, setNotes] = useState(lesson?.publishingNotes || "");

  useEffect(() => {
    setNotes(lesson?.publishingNotes || "");
  }, [lesson?.id, lesson?.publishingNotes]);

  const stats = {
    totalSchools: schools.length,
    totalAccounts: accounts.length,
    totalStudents: accounts.filter(a => a.role === "student").length,
    totalLessons: lessons.length,
    publishedLessons: lessons.filter(l => l.publicationStatus === "published").length,
    draftLessons: lessons.filter(l => l.publicationStatus === "draft").length,
    totalTopics: topics.length,
    topicsWithoutPrereqs: topics.filter(t => t.prerequisiteTopicIds.length === 0).length,
    totalMaterials: materials.length,
    approvedMaterials: materials.filter(m => m.sourceAuthority === "approved").length
  };

  const pendingActions = [];
  if (stats.draftLessons > 0) pendingActions.push({ type: "warning", text: `${stats.draftLessons} lessons in draft` });
  if (stats.topicsWithoutPrereqs > 0) pendingActions.push({ type: "error", text: `${stats.topicsWithoutPrereqs} topics missing prerequisites` });
  if (!governanceSummary?.canPublish && lesson) pendingActions.push({ type: "warning", text: "Selected lesson cannot be published" });

  const alerts = [];
  const orphanedMaterials = materials.filter(m => !lessons.some(l => l.sourceMaterialIds.includes(m.id)));
  if (orphanedMaterials.length > 0) alerts.push({ type: "warning", text: `${orphanedMaterials.length} materials not attached to any lesson` });

  const contentBySubject = subjects.map(subject => ({
    name: subject.name,
    topicCount: topics.filter(t => t.subjectId === subject.id).length,
    lessonCount: lessons.filter(l => l.subjectId === subject.id).length,
    materialCount: materials.filter(m => m.subjectId === subject.id).length
  }));

  const apiOk = apiHealth.filter(a => a.status === "ready").length;
  const apiTotal = apiHealth.length;

  return (
    <Space direction="vertical" size={16} style={{ display: "flex", width: "100%" }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card title="System Health" size="small">
            <List
              size="small"
              dataSource={apiHealth}
              renderItem={(api) => (
                <List.Item>
                  <Space>
                    <Tag color={api.status === "ready" ? "green" : api.status === "missing" ? "red" : "gold"}>
                      {api.status}
                    </Tag>
                    <Text>{api.domain}</Text>
                  </Space>
                </List.Item>
              )}
            />
          </Card>
        </Col>
        <Col xs={24} md={16}>
          <Card title="Quick Stats" size="small">
            <Row gutter={[8, 8]}>
              <Col span={6}>
                <Statistic title="Schools" value={stats.totalSchools} />
              </Col>
              <Col span={6}>
                <Statistic title="Students" value={stats.totalStudents} />
              </Col>
              <Col span={6}>
                <Statistic title="Accounts" value={stats.totalAccounts} />
              </Col>
              <Col span={6}>
                <Statistic title="Lessons" value={stats.totalLessons} valueStyle={{ color: "#114b5f" }} />
              </Col>
              <Col span={6}>
                <Statistic title="Published" value={stats.publishedLessons} valueStyle={{ color: "#2f855a" }} />
              </Col>
              <Col span={6}>
                <Statistic title="Drafts" value={stats.draftLessons} valueStyle={{ color: "#d69e2e" }} />
              </Col>
              <Col span={6}>
                <Statistic title="Materials" value={stats.totalMaterials} />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="Pending Actions" size="small">
            {pendingActions.length > 0 ? (
              <List
                size="small"
                dataSource={pendingActions}
                renderItem={(action) => (
                  <List.Item>
                    <Tag color={action.type === "error" ? "red" : "gold"}>{action.type}</Tag>
                    <Text>{action.text}</Text>
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="No pending actions" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Alerts" size="small">
            {alerts.length > 0 ? (
              <List
                size="small"
                dataSource={alerts}
                renderItem={(alert) => (
                  <List.Item>
                    <Tag color={alert.type === "error" ? "red" : "gold"}>{alert.type}</Tag>
                    <Text>{alert.text}</Text>
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="No alerts" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Content Overview" size="small">
            <Table
              size="small"
              pagination={false}
              dataSource={contentBySubject}
              columns={[
                { title: "Subject", dataIndex: "name", key: "name" },
                { title: "Topics", dataIndex: "topicCount", key: "topicCount", align: "right" as const },
                { title: "Lessons", dataIndex: "lessonCount", key: "lessonCount", align: "right" as const },
                { title: "Materials", dataIndex: "materialCount", key: "materialCount", align: "right" as const }
              ]}
              rowKey="name"
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Publishing Readiness" size="small">
            <List
              size="small"
              dataSource={[
                { label: "Transcript", ok: governanceSummary?.transcriptReady },
                { label: "Summary", ok: governanceSummary?.summaryReady },
                { label: "Approved materials", ok: (governanceSummary?.approvedMaterialCount || 0) > 0 },
                { label: "Prerequisites", ok: (governanceSummary?.prerequisiteCount || 0) > 0 },
                { label: "Lesson dependencies", ok: (governanceSummary?.lessonDependencyCount || 0) > 0 }
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Space>
                    <Tag color={item.ok ? "green" : "red"}>{item.ok ? "ready" : "missing"}</Tag>
                    <Text>{item.label}</Text>
                  </Space>
                </List.Item>
              )}
            />
            <Space direction="vertical" size={8} style={{ display: "flex", marginTop: 12 }}>
              <Input.TextArea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Publishing notes"
              />
              <Space>
                <Button
                  type="primary"
                  size="small"
                  disabled={!governanceSummary?.canPublish}
                  onClick={() => void onPublish("published", notes)}
                >
                  Publish
                </Button>
                <Button size="small" onClick={() => void onPublish("draft", notes)}>
                  Draft
                </Button>
                <Tag color={lesson?.publicationStatus === "published" ? "green" : "gold"}>
                  {lesson?.publicationStatus || "draft"}
                </Tag>
              </Space>
            </Space>
          </Card>
        </Col>
      </Row>
    </Space>
  );
}

function OverviewWorkspace({
  lesson,
  topic,
  account,
  subjects,
  enrolledSubjectIds,
  selectedSubjectId,
  onSubjectChange,
  topics
}: {
  account?: UserAccount;
  lesson?: Lesson;
  topic?: Topic;
  subjects: Subject[];
  enrolledSubjectIds: string[];
  selectedSubjectId?: string;
  onSubjectChange?: (key: string) => void;
  topics: Topic[];
}) {
  const isStudent = account?.role === "student";
  const topicSummary = lesson?.summary?.highLevelSummary?.trim();
  const conceptItems = lesson?.summary?.conceptBreakdown || [];
  const subjectTopics = (selectedSubjectId
    ? topics.filter((t) => t.subjectId === selectedSubjectId)
    : topics
  ).slice(0, 12);

  return (
    <>
      {isStudent && subjects.length > 0 && selectedSubjectId && onSubjectChange && (
        <Tabs
          activeKey={selectedSubjectId}
          onChange={onSubjectChange}
          items={subjects.map((subject) => ({
            key: subject.id,
            label: subject.name
          }))}
          style={{ marginBottom: 16 }}
        />
      )}
    <Row gutter={[16, 16]}>
      <Col xs={24} xl={16}>
        <Card title="Topics covered">
          {topicSummary ? <Paragraph>{topicSummary}</Paragraph> : null}
          {conceptItems.length > 0 ? (
            <List
              dataSource={conceptItems}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta title={item.title} description={item.description} />
                </List.Item>
              )}
            />
          ) : subjectTopics.length > 0 ? (
            <List
              dataSource={subjectTopics}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={item.name}
                    description={item.description || "Topic available for this subject."}
                  />
                </List.Item>
              )}
            />
          ) : (
            <Empty description="No topic breakdown available yet." image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Card>
        <Card title="Understanding status" style={{ marginTop: 16 }}>
          <Paragraph type="secondary">
            Understanding and mastery details have moved to the dedicated
            <Text strong> Understanding Map</Text> workspace for a cleaner Overview page.
          </Paragraph>
          <Button type="primary" onClick={() => window.location.assign('/understanding-map')}>
            Open Understanding Map
          </Button>
        </Card>
      </Col>
      <Col xs={24} xl={8} />
    </Row>
    </>
  );
}

function MaterialsWorkspace({
  materials,
  topics,
  subjects,
  schools,
  onCreateMaterial,
  onUpdateMaterial,
  onDeleteMaterial,
  onBulkDeleteMaterial,
  superadminMode,
  refreshBootstrap,
  currentUser
}: {
  materials: StudyMaterial[];
  topics: Topic[];
  subjects: Subject[];
  schools: School[];
  onCreateMaterial: (values: {
    title: string;
    excerpt: string;
    citation?: string;
    materialType: StudyMaterial["materialType"];
    sourceAuthority: StudyMaterial["sourceAuthority"];
    file: File;
    schoolId?: string | null;
    subjectId: string;
    topicIds: string[];
  }) => Promise<void>;
  onUpdateMaterial: (
    materialId: string,
    patch: Partial<Omit<StudyMaterial, "id">>
  ) => Promise<void>;
  onDeleteMaterial: (materialId: string) => Promise<void>;
  onBulkDeleteMaterial: (materialIds: string[]) => Promise<void>;
  superadminMode: boolean;
  refreshBootstrap: () => Promise<void>;
  currentUser: UserAccount | null;
}) {
  const [form] = Form.useForm();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [schoolFilter, setSchoolFilter] = useState<string>("all");
  const [editingMaterial, setEditingMaterial] = useState<StudyMaterial | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const isStudent = currentUser?.role === "student";
  const schoolLookup = new Map(schools.map(s => [s.id, s]));
  const [selectedSubjectId, setSelectedSubjectId] = useState<string>(
    isStudent && subjects.length > 0 ? subjects[0].id : ""
  );
  const subjectTopics = topics.filter(t => t.subjectId === selectedSubjectId);

  const filteredMaterials = materials.filter(m => {
    if (isStudent) {
      if (!selectedSubjectId) return true;
      return m.subjectId === selectedSubjectId;
    }
    if (schoolFilter === "all") return true;
    if (schoolFilter === "global") return !m.schoolId;
    return m.schoolId === schoolFilter;
  });

  const isSyncing = (record: StudyMaterial): boolean => record.openNotebookSyncStatus === "pending";

  const canDelete = (record: StudyMaterial): boolean => {
    if (isSyncing(record)) return false;
    if (superadminMode) return true;
    if (isStudent && currentUser && (!record.uploadedBy || record.uploadedBy === currentUser.id)) return true;
    return false;
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
    getCheckboxProps: (record: StudyMaterial) => ({
      disabled: !canDelete(record)
    })
  };

  const columns: TableColumnsType<StudyMaterial> = [
    ...(!isStudent
      ? [{
          title: "School",
          key: "school",
          width: 150,
          render: (_: unknown, record: StudyMaterial) => (
            record.schoolId ? (
              <Tag color="blue">{schoolLookup.get(record.schoolId)?.name || record.schoolId}</Tag>
            ) : (
              <Tag color="green">Global</Tag>
            )
          )
        } as TableColumnsType<StudyMaterial>[number]]
      : []),
    {
      title: "Title",
      dataIndex: "title",
      key: "title"
    },
    ...(!isStudent
      ? [{
          title: "Type",
          dataIndex: "materialType",
          key: "materialType",
          render: (value: StudyMaterial["materialType"]) => <Tag>{value}</Tag>
        } as TableColumnsType<StudyMaterial>[number]]
      : []),
    ...(!isStudent
      ? [{
          title: "Authority",
          dataIndex: "sourceAuthority",
          key: "sourceAuthority",
          render: (value: StudyMaterial["sourceAuthority"]) => (
            <Tag color={value === "approved" ? "green" : "gold"}>{value}</Tag>
          )
        } as TableColumnsType<StudyMaterial>[number]]
      : []),
    {
      title: "Sync",
      key: "sync",
      width: 120,
      render: (_, record) => (
        <Tag 
          color={syncStatusColor(record.openNotebookSyncStatus)}
          style={{ cursor: record.openNotebookSyncStatus === "failed" ? "pointer" : "default" }}
          onClick={record.openNotebookSyncStatus === "failed" ? () => {
            void retrySyncOpenNotebook(record.id).then((result) => {
              if (result) {
                void refreshBootstrap();
              }
            });
          } : undefined}
        >
          {getSyncStatusLabel(record.openNotebookSyncStatus)}
        </Tag>
      )
    },
    ...(!isStudent
      ? [{
          title: "Topics",
          key: "topics",
          render: (_, record) => {
            const materialTopicIds = record.topicIds || [];
            return (
              <Space wrap size={[4, 4]}>
                {materialTopicIds.slice(0, 3).map((topicId) => (
                  <Tag key={topicId}>{topics.find((topic) => topic.id === topicId)?.name || topicId}</Tag>
                ))}
                {materialTopicIds.length > 3 && (
                  <Tag>+{materialTopicIds.length - 3}</Tag>
                )}
              </Space>
            );
          }
        } as TableColumnsType<StudyMaterial>[number]]
      : []),
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_, record) => {
        const isOwnMaterial = isStudent && currentUser && (!record.uploadedBy || record.uploadedBy === currentUser.id);
        if (!superadminMode && !isOwnMaterial) return null;
        return (
          <Space>
            {superadminMode && (
              <>
                <Button size="small" onClick={() => {
                  setEditingMaterial(record);
                  setIsEditModalOpen(true);
                }}>
                  Edit
                </Button>
                <Button
                  size="small"
                  onClick={() =>
                    void onUpdateMaterial(record.id, {
                      sourceAuthority:
                        record.sourceAuthority === "approved" ? "draft" : "approved"
                    })
                  }
                >
                  {record.sourceAuthority === "approved" ? "Draft" : "Approve"}
                </Button>
              </>
            )}
            <Button danger size="small" disabled={isSyncing(record)} onClick={() => {
              Modal.confirm({
                title: "Delete this material?",
                content: `Are you sure you want to delete "${record.title}"? This action cannot be undone.`,
                okText: "Delete",
                okType: "danger",
                onOk: () => onDeleteMaterial(record.id)
              });
            }}>
              {isSyncing(record) ? "Syncing..." : "Delete"}
            </Button>
          </Space>
        );
      }
    }
  ];

  const schoolOptions = [
    { value: "all", label: "All schools" },
    { value: "global", label: "Global (no school)" },
    ...schools.map(s => ({ value: s.id, label: s.name }))
  ];

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card
            title="Study Materials"
            extra={
              <Space>
                {!isStudent && (
                  <Select
                    value={schoolFilter}
                    onChange={setSchoolFilter}
                    options={schoolOptions}
                    style={{ minWidth: 180 }}
                  />
                )}
                <Tag color="processing">{filteredMaterials.length} materials</Tag>
                {selectedRowKeys.length > 0 && (
                  <Button
                    danger
                    onClick={() => {
                      Modal.confirm({
                        title: "Delete selected materials?",
                        content: `Are you sure you want to delete ${selectedRowKeys.length} material(s)? This action cannot be undone.`,
                        okText: "Delete",
                        okType: "danger",
                        onOk: () => {
                          const ids = selectedRowKeys.map(String);
                          setSelectedRowKeys([]);
                          return onBulkDeleteMaterial(ids);
                        }
                      });
                    }}
                  >
                    Delete Selected ({selectedRowKeys.length})
                  </Button>
                )}
                <Button type="primary" onClick={() => setIsModalOpen(true)} disabled={!currentUser}>
                  Add Material
                </Button>
              </Space>
            }
          >
            {isStudent ? (
              <>
                <Paragraph type="secondary">
                  Upload your own study materials. These are private to you and will be used as context when you chat with the learning assistant.
                </Paragraph>
                {subjects.length > 0 && (
                  <Tabs
                    activeKey={selectedSubjectId}
                    onChange={setSelectedSubjectId}
                    items={subjects.map((subject) => ({
                      key: subject.id,
                      label: subject.name
                    }))}
                  />
                )}
              </>
            ) : (
              <Paragraph type="secondary">
                Materials are scoped per school. Global materials are available to all schools unless overridden.
              </Paragraph>
            )}
            <Table rowKey="id" rowSelection={rowSelection} pagination={{ pageSize: 10 }} columns={columns} dataSource={filteredMaterials} />
          </Card>
        </Col>
      </Row>
    <Modal
      title="Add study material"
      open={isModalOpen}
      onCancel={() => {
        setIsModalOpen(false);
        setSelectedFile(null);
        form.resetFields();
      }}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          sourceAuthority: "draft"
        }}
        onFinish={async (values) => {
          if (!selectedFile) {
            message.error("Choose a file to upload.");
            return;
          }

          const materialType = getMaterialTypeFromFile(selectedFile);
          const subjectId = isStudent ? selectedSubjectId : values.subjectId;
          await onCreateMaterial({ ...values, subjectId, file: selectedFile, materialType });
          setSelectedFile(null);
          form.resetFields();
          setIsModalOpen(false);
        }}
      >
        <Form.Item name="title" label="Title" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        {isStudent ? (
          <Form.Item label="Subject">
            <Input value={subjects.find(s => s.id === selectedSubjectId)?.name} disabled />
          </Form.Item>
        ) : (
          <Form.Item name="subjectId" label="Subject" rules={[{ required: true, message: "Select a subject" }]}>
            <Select
              placeholder="Select a subject"
              options={subjects.map(s => ({ value: s.id, label: s.name }))}
              onChange={(value) => {
                setSelectedSubjectId(value);
                form.setFieldValue("topicIds", []);
              }}
            />
          </Form.Item>
        )}
        {!isStudent && (
          <Form.Item
            name="topicIds"
            label="Topics"
            extra="Optional for textbooks - topics will be auto-extracted from content"
          >
            <Select
              mode="multiple"
              placeholder={selectedSubjectId ? "Select topics (optional)" : "Select a subject first"}
              options={subjectTopics.map(t => ({ value: t.id, label: t.name }))}
              disabled={!selectedSubjectId}
            />
          </Form.Item>
        )}
        {!isStudent && (
          <Form.Item name="excerpt" label="Excerpt" rules={[{ required: true }]}>
            <Input.TextArea rows={4} />
          </Form.Item>
        )}
        {!isStudent && (
          <Form.Item name="citation" label="Citation">
            <Input />
          </Form.Item>
        )}
        <Form.Item
          name="file"
          label="Upload file"
          required
          extra="Supported: PDF, PPT/PPTX, DOC/DOCX/TXT/RTF, PNG/JPG/GIF/WEBP/SVG"
        >
          <Upload.Dragger
            accept=".pdf,.ppt,.pptx,.doc,.docx,.txt,.rtf,.png,.jpg,.jpeg,.gif,.webp,.svg"
            beforeUpload={(file) => {
              setSelectedFile(file);
              return false;
            }}
            onChange={(info) => {
              if (info.fileList.length > 0) {
                const originFile = info.fileList[0].originFileObj;
                if (originFile) {
                  setSelectedFile(originFile as File);
                }
              } else {
                setSelectedFile(null);
              }
            }}
            onRemove={() => {
              setSelectedFile(null);
            }}
            maxCount={1}
          >
            <p style={{ margin: 0 }}>
              <InboxOutlined style={{ fontSize: 24, color: "#1890ff" }} />
            </p>
            <p style={{ margin: "8px 0 0" }}>Click or drag file to upload</p>
          </Upload.Dragger>
        </Form.Item>
        {!isStudent && (
          <Form.Item name="schoolId" label="School Scope" extra="Leave empty for global material available to all schools">
            <Select
              allowClear
              placeholder="Select school (or leave empty for global)"
              options={schools.map(s => ({ value: s.id, label: s.name }))}
            />
          </Form.Item>
        )}
        {!isStudent && (
          <Form.Item name="sourceAuthority" label="Authority" rules={[{ required: true }]}>
            <Select
              options={[
                { value: "draft", label: "Draft" },
                { value: "approved", label: "Approved" }
              ]}
            />
          </Form.Item>
        )}
        <Button type="primary" htmlType="submit" block disabled={!selectedFile}>
          Create material
        </Button>
      </Form>
    </Modal>

    <Modal
      title="Edit Material"
      open={isEditModalOpen}
      onCancel={() => {
        setIsEditModalOpen(false);
        setEditingMaterial(null);
      }}
      footer={null}
    >
      {editingMaterial && (
        <Form
          layout="vertical"
          initialValues={{
            title: editingMaterial.title,
            excerpt: editingMaterial.excerpt,
            citation: editingMaterial.citation,
            materialType: editingMaterial.materialType,
            sourceAuthority: editingMaterial.sourceAuthority,
            schoolId: editingMaterial.schoolId
          }}
          onFinish={async (values) => {
            await onUpdateMaterial(editingMaterial.id, {
              ...values,
              schoolId: values.schoolId || null
            });
            setIsEditModalOpen(false);
            setEditingMaterial(null);
            message.success("Material updated");
          }}
        >
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="excerpt" label="Excerpt" rules={[{ required: true }]}>
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item name="citation" label="Citation">
            <Input />
          </Form.Item>
          {!isStudent && (
            <Form.Item name="schoolId" label="School Scope" extra="Leave empty for global material">
              <Select
                allowClear
                placeholder="Select school (or leave empty for global)"
                options={schools.map(s => ({ value: s.id, label: s.name }))}
              />
            </Form.Item>
          )}
          {!isStudent && (
            <Form.Item name="sourceAuthority" label="Authority">
              <Select
                options={[
                  { value: "draft", label: "Draft" },
                  { value: "approved", label: "Approved" }
                ]}
              />
            </Form.Item>
          )}
          <Button type="primary" htmlType="submit" block>
            Save Changes
          </Button>
        </Form>
      )}
    </Modal>
    </>
  );
}

function TopicGraphWorkspace({
  topic,
  topicLookup,
  allTopics,
  lesson,
  allLessons,
  onSaveTopic,
  onSaveLessonDependencies,
  superadminMode
}: {
  topic?: Topic;
  topicLookup: Map<string, Topic>;
  allTopics: Topic[];
  lesson?: Lesson;
  allLessons: Lesson[];
  onSaveTopic: (values: {
    name: string;
    description: string;
    understandingGoal: string;
    prerequisiteTopicIds: string[];
  }) => Promise<void>;
  onSaveLessonDependencies: (values: { lessonDependencyIds: string[] }) => Promise<void>;
  superadminMode: boolean;
}) {
  const [form] = Form.useForm();
  const [lessonForm] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue({
      name: topic?.name,
      description: topic?.description,
      understandingGoal: topic?.understandingGoal,
      prerequisiteTopicIds: topic?.prerequisiteTopicIds || []
    });
  }, [form, topic]);

  useEffect(() => {
    lessonForm.setFieldsValue({
      lessonDependencyIds: lesson?.lessonDependencyIds || []
    });
  }, [lessonForm, lesson]);

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} xl={12}>
        <Card
          title="Understanding prerequisites"
          extra={<Tag color="purple">Required for comprehension</Tag>}
        >
          <Paragraph>
            The prerequisite graph should answer what a student must already
            understand before the current topic can make sense.
          </Paragraph>
          <Timeline
            items={[
              {
                children: (
                  <strong>
                    {topic?.name || "Current topic"}: {topic?.description || "Loading topic"}
                  </strong>
                )
              },
              ...(topic?.prerequisiteTopicIds || []).map((prerequisiteId) => ({
                color: "red",
                children: `${topicLookup.get(prerequisiteId)?.name || prerequisiteId}: ${
                  topicLookup.get(prerequisiteId)?.understandingGoal ||
                  "Prerequisite understanding objective"
                }`
              }))
            ]}
          />
        </Card>
      </Col>
      <Col xs={24} xl={12}>
        <Card title="Edit topic governance">
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => void onSaveTopic(values)}
          >
            <Form.Item name="name" label="Topic name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="description" label="Description" rules={[{ required: true }]}>
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item
              name="understandingGoal"
              label="Understanding goal"
              rules={[{ required: true }]}
            >
              <Input.TextArea rows={4} />
            </Form.Item>
            <Form.Item name="prerequisiteTopicIds" label="Prerequisite topics">
              <Select
                mode="multiple"
                options={allTopics
                  .filter((item) => item.id !== topic?.id)
                  .map((item) => ({ value: item.id, label: item.name }))}
              />
            </Form.Item>
            <Button type="primary" htmlType="submit" block disabled={!superadminMode}>
              Save topic configuration
            </Button>
          </Form>
        </Card>
        <Card title="Lesson dependency map" style={{ marginTop: 16 }}>
          <Paragraph>
            Map which prior lessons should be completed before this lesson is published or assigned.
          </Paragraph>
          <Form
            form={lessonForm}
            layout="vertical"
            onFinish={(values) => void onSaveLessonDependencies(values)}
          >
            <Form.Item name="lessonDependencyIds" label="Required earlier lessons">
              <Select
                mode="multiple"
                options={allLessons
                  .filter((item) => item.id !== lesson?.id)
                  .map((item) => ({ value: item.id, label: item.title }))}
              />
            </Form.Item>
            <Button type="primary" htmlType="submit" block disabled={!superadminMode || !lesson}>
              Save lesson dependency map
            </Button>
          </Form>
        </Card>
      </Col>
    </Row>
  );
}

function TopicGraphEditor({
  topics,
  subjects,
  onCreateTopic,
  onUpdateTopic,
  superadminMode
}: {
  topics: Topic[];
  subjects: Subject[];
  onCreateTopic: (values: {
    name: string;
    subjectId: string;
    description: string;
    understandingGoal: string;
    prerequisiteTopicIds: string[];
  }) => Promise<void>;
  onUpdateTopic: (topicId: string, values: {
    name: string;
    subjectId: string;
    description: string;
    understandingGoal: string;
    prerequisiteTopicIds: string[];
  }) => Promise<void>;
  superadminMode: boolean;
}) {
  const [selectedSubjectId, setSelectedSubjectId] = useState<string>("");
  const [nodePositions, setNodePositions] = useState<Record<string, { x: number; y: number }>>({});
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [addMenuPosition, setAddMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [newNodeName, setNewNodeName] = useState("");
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [pendingConnectionType, setPendingConnectionType] = useState<"prerequisite" | "dependant" | "first" | null>(null);
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [connectingFrom, setConnectingFrom] = useState<{ nodeId: string; x: number; y: number } | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [connectionModalData, setConnectionModalData] = useState<{ from: string; to: string } | null>(null);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  const subjectTopics = topics.filter((t) => t.subjectId === selectedSubjectId);
  const topicLookup = new Map(topics.map((t) => [t.id, t]));

  useEffect(() => {
    if (subjects.length > 0 && !selectedSubjectId) {
      setSelectedSubjectId(subjects[0].id);
    }
  }, [subjects, selectedSubjectId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (connectingFrom) {
          setConnectingFrom(null);
        } else if (editingNodeId) {
          setEditingNodeId(null);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [connectingFrom, editingNodeId]);

  useEffect(() => {
    const existingIds = new Set(subjectTopics.map((t) => t.id));
    const newPositions: Record<string, { x: number; y: number }> = {};
    let hasChanges = false;

    subjectTopics.forEach((topic, index) => {
      if (nodePositions[topic.id]) {
        newPositions[topic.id] = nodePositions[topic.id];
      } else {
        const cols = 4;
        const row = Math.floor(index / cols);
        const col = index % cols;
        newPositions[topic.id] = { x: 100 + col * 200, y: 100 + row * 150 };
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setNodePositions((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((id) => {
          if (!existingIds.has(id)) delete updated[id];
        });
        return { ...updated, ...newPositions };
      });
    }
  }, [subjectTopics]);

  const getConnections = () => {
    const connections: Array<{ from: string; to: string; type: "prerequisite" | "dependant" }> = [];
    subjectTopics.forEach((topic) => {
      (topic.prerequisiteTopicIds || []).forEach((prereqId) => {
        connections.push({ from: prereqId, to: topic.id, type: "prerequisite" });
      });
    });
    return connections;
  };

  const handleNodeClick = (e: React.MouseEvent, topicId: string) => {
    e.stopPropagation();
    if (editingNodeId === topicId) return;
    
    if (connectingFrom && connectingFrom.nodeId !== topicId) {
      handleEndConnection(topicId);
      return;
    }
    
    setSelectedNodeId(topicId);
    setAddMenuPosition(null);
  };

  const handleNodeDoubleClick = (e: React.MouseEvent, topicId: string) => {
    e.stopPropagation();
    if (!superadminMode) return;
    const topic = topicLookup.get(topicId);
    if (topic) {
      setEditingNodeId(topicId);
      setEditingName(topic.name);
    }
  };

  const handleSaveName = async () => {
    if (!editingNodeId || !editingName.trim()) {
      setEditingNodeId(null);
      return;
    }
    const topic = topicLookup.get(editingNodeId);
    if (topic) {
      await onUpdateTopic(editingNodeId, {
        name: editingName.trim(),
        subjectId: topic.subjectId,
        description: topic.description,
        understandingGoal: topic.understandingGoal,
        prerequisiteTopicIds: topic.prerequisiteTopicIds || []
      });
    }
    setEditingNodeId(null);
  };

  const handleAddClick = (e: React.MouseEvent, topicId: string) => {
    e.stopPropagation();
    setSelectedNodeId(topicId);
    setAddMenuPosition({ x: e.clientX, y: e.clientY });
  };

  const handleAddOption = (type: "prerequisite" | "dependant") => {
    setAddMenuPosition(null);
    setPendingConnectionType(type);
    setNewNodeName("");
    setIsNameModalOpen(true);
  };

  const handleCreateNode = async () => {
    if (!newNodeName.trim()) return;

    try {
      if (pendingConnectionType === "first" || !selectedNodeId) {
        await onCreateTopic({
          name: newNodeName.trim(),
          subjectId: selectedSubjectId,
          description: "",
          understandingGoal: "",
          prerequisiteTopicIds: []
        });
      } else if (pendingConnectionType === "prerequisite") {
        await onCreateTopic({
          name: newNodeName.trim(),
          subjectId: selectedSubjectId,
          description: "",
          understandingGoal: "",
          prerequisiteTopicIds: []
        });
        const newTopic = topics.find((t) => t.name === newNodeName.trim() && t.subjectId === selectedSubjectId);
        if (newTopic) {
          const parentTopic = topicLookup.get(selectedNodeId);
          if (parentTopic) {
            await onUpdateTopic(selectedNodeId, {
              name: parentTopic.name,
              subjectId: parentTopic.subjectId,
              description: parentTopic.description,
              understandingGoal: parentTopic.understandingGoal,
              prerequisiteTopicIds: [...(parentTopic.prerequisiteTopicIds || []), newTopic.id]
            });
          }
        }
      } else if (pendingConnectionType === "dependant") {
        await onCreateTopic({
          name: newNodeName.trim(),
          subjectId: selectedSubjectId,
          description: "",
          understandingGoal: "",
          prerequisiteTopicIds: [selectedNodeId]
        });
      }
      setIsNameModalOpen(false);
      setPendingConnectionType(null);
    } catch (err) {
      console.error("Failed to create node:", err);
    }
  };

  const handleMouseDown = (e: React.MouseEvent, topicId: string) => {
    if ((e.target as HTMLElement).closest(".add-btn")) return;
    if ((e.target as HTMLElement).closest(".edit-input")) return;
    e.stopPropagation();
    setDraggingNodeId(topicId);
    const pos = nodePositions[topicId] || { x: 0, y: 0 };
    setDragOffset({
      x: e.clientX - pos.x,
      y: e.clientY - pos.y
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePos({ x: e.clientX, y: e.clientY });
    if (connectingFrom) return;
    if (!draggingNodeId) return;
    setNodePositions((prev) => ({
      ...prev,
      [draggingNodeId]: {
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      }
    }));
  };

  const handleMouseUp = () => {
    setDraggingNodeId(null);
  };

  const handleCanvasClick = () => {
    if (editingNodeId) return;
    setSelectedNodeId(null);
    setAddMenuPosition(null);
    setConnectingFrom(null);
  };

  const handleStartConnection = (e: React.MouseEvent, topicId: string) => {
    e.stopPropagation();
    if (!superadminMode) return;
    const pos = nodePositions[topicId] || { x: 0, y: 0 };
    setConnectingFrom({ nodeId: topicId, x: pos.x + 70, y: pos.y + 30 });
  };

  const handleEndConnection = async (topicId: string) => {
    if (!connectingFrom || connectingFrom.nodeId === topicId) {
      setConnectingFrom(null);
      return;
    }
    setConnectionModalData({ from: connectingFrom.nodeId, to: topicId });
    setConnectingFrom(null);
  };

  const handleConnectionTypeSelect = async (type: "prerequisite" | "dependant") => {
    if (!connectionModalData) return;
    const { from, to } = connectionModalData;
    const fromTopic = topicLookup.get(from);
    const toTopic = topicLookup.get(to);
    if (!fromTopic || !toTopic) return;

    try {
      if (type === "prerequisite") {
        await onUpdateTopic(to, {
          name: toTopic.name,
          subjectId: toTopic.subjectId,
          description: toTopic.description,
          understandingGoal: toTopic.understandingGoal,
          prerequisiteTopicIds: [...new Set([...(toTopic.prerequisiteTopicIds || []), from])]
        });
      } else {
        await onUpdateTopic(from, {
          name: fromTopic.name,
          subjectId: fromTopic.subjectId,
          description: fromTopic.description,
          understandingGoal: fromTopic.understandingGoal,
          prerequisiteTopicIds: [...new Set([...(fromTopic.prerequisiteTopicIds || []), to])]
        });
      }
    } catch (err) {
      console.error("Failed to create connection:", err);
    }
    setConnectionModalData(null);
  };

  const connections = getConnections();
  const NODE_WIDTH = 140;
  const NODE_HEIGHT = 60;

  const maxNodeY = Math.max(...Object.values(nodePositions).map((p) => p.y), 0);
  const canvasHeight = Math.max(600, maxNodeY + 200);

  return (
    <>
      <Card
        title="Topic Graph Editor"
        extra={
          <Select
            value={selectedSubjectId}
            onChange={(value) => setSelectedSubjectId(value)}
            style={{ minWidth: 200 }}
            options={subjects.map((s) => ({ value: s.id, label: s.name }))}
          />
        }
      >
        {connectingFrom && (
          <Alert
            type="warning"
            message={
              <span>
                Connection mode: Click on another topic to connect. Press{" "}
                <strong>Escape</strong> to cancel.
              </span>
            }
            closable
            onClose={() => setConnectingFrom(null)}
            style={{ marginBottom: 16 }}
          />
        )}
        <div
          ref={canvasRef}
          onClick={handleCanvasClick}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{
            position: "relative",
            width: "100%",
            minHeight: 600,
            maxHeight: 800,
            height: canvasHeight,
            background: "#fafafa",
            borderRadius: 8,
            overflow: "auto",
            cursor: draggingNodeId ? "grabbing" : connectingFrom ? "crosshair" : "default",
            userSelect: "none"
          }}
        >
          <svg
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: canvasHeight,
              minHeight: 600,
              pointerEvents: "none"
            }}
          >
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
              >
                <polygon points="0 0, 10 3.5, 0 7" fill="#1890ff" />
              </marker>
              <marker
                id="arrowhead-prereq"
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
              >
                <polygon points="0 0, 10 3.5, 0 7" fill="#52c41a" />
              </marker>
            </defs>
            {connections.map((conn, idx) => {
              const fromPos = nodePositions[conn.from];
              const toPos = nodePositions[conn.to];
              if (!fromPos || !toPos) return null;

              const fromX = fromPos.x + NODE_WIDTH / 2;
              const fromY = fromPos.y + NODE_HEIGHT;
              const toX = toPos.x + NODE_WIDTH / 2;
              const toY = toPos.y;

              const midY = (fromY + toY) / 2;
              const path = `M ${fromX} ${fromY} C ${fromX} ${midY}, ${toX} ${midY}, ${toX} ${toY}`;

              return (
                <g key={idx}>
                  <path
                    d={path}
                    stroke={conn.type === "prerequisite" ? "#52c41a" : "#1890ff"}
                    strokeWidth={2}
                    fill="none"
                    markerEnd={conn.type === "prerequisite" ? "url(#arrowhead-prereq)" : "url(#arrowhead)"}
                  />
                  <text
                    x={(fromX + toX) / 2}
                    y={midY - 8}
                    textAnchor="middle"
                    fontSize={10}
                    fill="#666"
                  >
                    {conn.type}
                  </text>
                </g>
              );
            })}
            {connectingFrom && (
              <line
                x1={connectingFrom.x}
                y1={connectingFrom.y}
                x2={mousePos.x - (canvasRef.current?.getBoundingClientRect().left || 0) + (canvasRef.current?.scrollLeft || 0)}
                y2={mousePos.y - (canvasRef.current?.getBoundingClientRect().top || 0) + (canvasRef.current?.scrollTop || 0)}
                stroke="#faad14"
                strokeWidth={2}
                strokeDasharray="5,5"
              />
            )}
          </svg>

          {subjectTopics.map((topic) => {
            const pos = nodePositions[topic.id] || { x: 0, y: 0 };
            const isSelected = selectedNodeId === topic.id;
            const isEditing = editingNodeId === topic.id;
            const isConnecting = connectingFrom?.nodeId === topic.id;
            const isValidTarget = connectingFrom && connectingFrom.nodeId !== topic.id;
            const isHoveredTarget = isValidTarget && hoveredNodeId === topic.id;

            return (
              <div
                key={topic.id}
                onClick={(e) => handleNodeClick(e, topic.id)}
                onDoubleClick={(e) => handleNodeDoubleClick(e, topic.id)}
                onMouseDown={(e) => handleMouseDown(e, topic.id)}
                onMouseEnter={() => setHoveredNodeId(topic.id)}
                onMouseLeave={() => setHoveredNodeId(null)}
                style={{
                  position: "absolute",
                  left: pos.x,
                  top: pos.y,
                  width: NODE_WIDTH,
                  height: NODE_HEIGHT,
                  borderRadius: "50%",
                  background: isConnecting ? "#faad14" : isHoveredTarget ? "#52c41a" : isSelected ? "#1890ff" : "#fff",
                  border: `3px solid ${isConnecting ? "#faad14" : isHoveredTarget ? "#52c41a" : isSelected ? "#1890ff" : "#d9d9d9"}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isEditing ? "text" : connectingFrom ? "pointer" : "grab",
                  boxShadow: isHoveredTarget ? "0 4px 16px rgba(82,196,26,0.4)" : "0 2px 8px rgba(0,0,0,0.15)",
                  transition: draggingNodeId === topic.id ? "none" : "box-shadow 0.2s, background 0.2s, border-color 0.2s",
                  zIndex: isSelected || isEditing || isConnecting || isHoveredTarget ? 10 : 1
                }}
              >
                {isEditing ? (
                  <input
                    className="edit-input"
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onBlur={handleSaveName}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveName();
                      if (e.key === "Escape") setEditingNodeId(null);
                    }}
                    autoFocus
                    style={{
                      width: NODE_WIDTH - 20,
                      border: "none",
                      background: "transparent",
                      textAlign: "center",
                      fontSize: 12,
                      fontWeight: 500,
                      outline: "none"
                    }}
                  />
                ) : (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: isSelected || isConnecting || isHoveredTarget ? "#fff" : "#333",
                      textAlign: "center",
                      padding: "0 8px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {topic.name}
                  </span>
                )}
                {superadminMode && !isEditing && !connectingFrom && (
                  <button
                    className="connect-btn"
                    onClick={(e) => handleStartConnection(e, topic.id)}
                    title="Click to connect to another topic"
                    style={{
                      position: "absolute",
                      bottom: -8,
                      left: -8,
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: "#1890ff",
                      border: "2px solid #fff",
                      color: "#fff",
                      fontSize: 16,
                      fontWeight: "bold",
                      cursor: "crosshair",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                    }}
                  >
                    ↔
                  </button>
                )}
                {superadminMode && !isEditing && !connectingFrom && (
                  <button
                    className="add-btn"
                    onClick={(e) => handleAddClick(e, topic.id)}
                    title="Add connected topic"
                    style={{
                      position: "absolute",
                      bottom: -8,
                      right: -8,
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: "#52c41a",
                      border: "2px solid #fff",
                      color: "#fff",
                      fontSize: 16,
                      fontWeight: "bold",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                    }}
                  >
                    +
                  </button>
                )}
                {!superadminMode && (
                  <div
                    onClick={() => handleEndConnection(topic.id)}
                    style={{
                      position: "absolute",
                      bottom: -8,
                      left: -8,
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: "#d9d9d9",
                      border: "2px solid #fff",
                      cursor: "pointer"
                    }}
                  />
                )}
              </div>
            );
          })}

          {subjectTopics.length === 0 && (
            <div style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              color: "#999"
            }}>
              <PlusCircleOutlined style={{ fontSize: 48, marginBottom: 16, color: "#d9d9d9" }} />
              <div style={{ marginBottom: 16 }}>No topics yet. Add your first topic to get started.</div>
              {superadminMode && (
                <Button
                  type="primary"
                  icon={<PlusCircleOutlined />}
                  onClick={() => {
                    setSelectedNodeId(null);
                    setPendingConnectionType("first");
                    setNewNodeName("");
                    setIsNameModalOpen(true);
                  }}
                >
                  Add First Topic
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      {addMenuPosition && (
        <div
          style={{
            position: "fixed",
            left: addMenuPosition.x,
            top: addMenuPosition.y,
            background: "#fff",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            padding: 8,
            zIndex: 1000
          }}
        >
          <div
            onClick={() => handleAddOption("prerequisite")}
            style={{
              padding: "8px 16px",
              cursor: "pointer",
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              gap: 8
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <ArrowUpOutlined style={{ color: "#52c41a" }} />
            <span>Prerequisite</span>
          </div>
          <div
            onClick={() => handleAddOption("dependant")}
            style={{
              padding: "8px 16px",
              cursor: "pointer",
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              gap: 8
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <ArrowDownOutlined style={{ color: "#1890ff" }} />
            <span>Dependant</span>
          </div>
        </div>
      )}

      <Modal
        title={pendingConnectionType === "first" ? "Add First Topic" : pendingConnectionType === "prerequisite" ? "Add Prerequisite Topic" : "Add Dependant Topic"}
        open={isNameModalOpen}
        onOk={handleCreateNode}
        onCancel={() => {
          setIsNameModalOpen(false);
          setPendingConnectionType(null);
        }}
        okText="Create"
      >
        <Input
          placeholder="Enter topic name"
          value={newNodeName}
          onChange={(e) => setNewNodeName(e.target.value)}
          onPressEnter={handleCreateNode}
          autoFocus
        />
        <div style={{ marginTop: 8, fontSize: 12, color: "#999" }}>
          {pendingConnectionType === "first"
            ? "This will be your first topic in this subject"
            : pendingConnectionType === "prerequisite"
            ? "This topic will be a prerequisite for the selected topic"
            : "This topic will depend on the selected topic"}
        </div>
      </Modal>

      <Modal
        title="Create Connection"
        open={!!connectionModalData}
        onCancel={() => setConnectionModalData(null)}
        footer={null}
        width={400}
      >
        <div style={{ padding: "16px 0" }}>
          <p style={{ marginBottom: 16 }}>
            How does <strong>{topicLookup.get(connectionModalData?.from || "")?.name}</strong> relate to{" "}
            <strong>{topicLookup.get(connectionModalData?.to || "")?.name}</strong>?
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Button
              block
              size="large"
              onClick={() => handleConnectionTypeSelect("prerequisite")}
              icon={<ArrowUpOutlined />}
              style={{ textAlign: "left" }}
            >
              <div>
                <div style={{ fontWeight: 500 }}>Prerequisite</div>
                <div style={{ fontSize: 12, color: "#999", fontWeight: "normal" }}>
                  "{topicLookup.get(connectionModalData?.from || "")?.name}" is a prerequisite for "{topicLookup.get(connectionModalData?.to || "")?.name}"
                </div>
              </div>
            </Button>
            <Button
              block
              size="large"
              onClick={() => handleConnectionTypeSelect("dependant")}
              icon={<ArrowDownOutlined />}
              style={{ textAlign: "left" }}
            >
              <div>
                <div style={{ fontWeight: 500 }}>Dependant</div>
                <div style={{ fontSize: 12, color: "#999", fontWeight: "normal" }}>
                  "{topicLookup.get(connectionModalData?.from || "")?.name}" is a dependant of "{topicLookup.get(connectionModalData?.to || "")?.name}"
                </div>
              </div>
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

function AccountsWorkspace({
  accounts,
  schools,
  onAssignRole,
  modalOpen,
  onOpenModal,
  onCloseModal,
  onCreateAccount
}: {
  accounts: UserAccount[];
  schools: School[];
  onAssignRole: (userId: string, role: UserAccount["role"]) => Promise<void>;
  modalOpen: boolean;
  onOpenModal: () => void;
  onCloseModal: () => void;
  onCreateAccount: (values: {
    fullName: string;
    email: string;
    role: UserAccount["role"];
    organizationName: string;
  }) => Promise<void>;
}) {
  const [form] = Form.useForm();
  const columns: TableColumnsType<UserAccount> = [
    { title: "Name", dataIndex: "fullName", key: "fullName" },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Organization", dataIndex: "organizationName", key: "organizationName" },
    {
      title: "Role",
      key: "role",
      render: (_, record) => (
        <Select
          value={record.role}
          style={{ minWidth: 160 }}
          options={[
            { value: "superadmin", label: "Superadmin" },
            { value: "student", label: "Student" }
          ]}
          onChange={(value) => void onAssignRole(record.id, value)}
        />
      )
    }
  ];

  return (
    <>
      <Card
        title="Accounts"
        extra={
          <Button type="primary" onClick={onOpenModal}>
            Add account
          </Button>
        }
      >
        <Paragraph>
          Provision student and additional superadmin accounts from one place,
          then assign or adjust their roles without impersonating them.
        </Paragraph>
        <Table rowKey="id" pagination={false} columns={columns} dataSource={accounts} />
      </Card>
      <Modal title="Create account" open={modalOpen} onCancel={onCloseModal} footer={null} destroyOnHidden>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Temporary password"
          description="New accounts are created with the temporary password changeme123."
        />
        <Form
          form={form}
          layout="vertical"
          initialValues={{ role: "student" }}
          onFinish={async (values) => {
            await onCreateAccount(values);
            form.resetFields();
          }}
        >
          <Form.Item name="fullName" label="Full name" rules={[{ required: true }]}>
            <Input placeholder="Nokuthula Dlamini" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true },
              { type: "email", message: "Enter a valid email address." }
            ]}
          >
            <Input placeholder="user@school.org" />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true }]}>
            <Select
              options={[
                { value: "student", label: "Student" },
                { value: "superadmin", label: "Superadmin" }
              ]}
            />
          </Form.Item>
          <Form.Item name="organizationName" label="School" rules={[{ required: true }]}>
            <Select
              showSearch
              options={schools.map((school) => ({
                value: school.name,
                label: `${school.name} (${school.shortCode})`
              }))}
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>
            Create account
          </Button>
        </Form>
      </Modal>
    </>
  );
}

function ApiStatusWorkspace({ apiHealth }: { apiHealth: ApiHealth[] }) {
  const columns: TableColumnsType<ApiHealth> = [
    { title: "Domain", dataIndex: "domain", key: "domain" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value: ApiHealth["status"]) => (
        <Tag color={value === "ready" ? "green" : value === "placeholder" ? "gold" : "red"}>
          {value}
        </Tag>
      )
    },
    { title: "Notes", dataIndex: "notes", key: "notes" }
  ];

  return (
    <Card title="API readiness">
      <Table rowKey="domain" pagination={false} columns={columns} dataSource={apiHealth} />
    </Card>
  );
}

function SchoolsWorkspace({
  schools,
  canManage,
  modalOpen,
  onOpenModal,
  onCloseModal,
  onCreateSchool,
  onDeleteSchool
}: {
  schools: School[];
  canManage: boolean;
  modalOpen: boolean;
  onOpenModal: () => void;
  onCloseModal: () => void;
  onCreateSchool: (values: {
    name: string;
    shortCode?: string;
    contactEmail?: string;
  }) => Promise<void>;
  onDeleteSchool: (schoolId: string) => Promise<void>;
}) {
  const [form] = Form.useForm();
  const columns: TableColumnsType<School> = [
    { title: "School", dataIndex: "name", key: "name" },
    { title: "Code", dataIndex: "shortCode", key: "shortCode", width: 120 },
    { title: "Contact", dataIndex: "contactEmail", key: "contactEmail" },
    {
      title: "Created",
      key: "createdAt",
      width: 140,
      render: (_, record) => new Date(record.createdAt).toLocaleDateString()
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_, record) =>
        canManage ? (
          <Button danger size="small" onClick={() => void onDeleteSchool(record.id)}>
            Delete
          </Button>
        ) : null
    }
  ];

  return (
    <>
      <Card
        title="Schools"
        extra={
          canManage ? (
            <Button type="primary" onClick={onOpenModal}>
              Add school
            </Button>
          ) : null
        }
      >
        <Paragraph>
          Schools governed by the superadmin are listed here and can be assigned to accounts.
        </Paragraph>
        <Table
          rowKey="id"
          pagination={false}
          columns={columns}
          dataSource={schools}
          locale={{ emptyText: "No schools created yet." }}
        />
      </Card>
      <Modal
        title="Add school"
        open={modalOpen}
        onCancel={onCloseModal}
        footer={null}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            await onCreateSchool(values);
            form.resetFields();
          }}
        >
          <Form.Item name="name" label="School name" rules={[{ required: true }]}>
            <Input placeholder="Khanyisa Secondary School" />
          </Form.Item>
          <Form.Item name="shortCode" label="Short code">
            <Input placeholder="KSS" maxLength={12} />
          </Form.Item>
          <Form.Item
            name="contactEmail"
            label="Contact email"
            rules={[{ type: "email", message: "Enter a valid email address." }]}
          >
            <Input placeholder="admin@school.org" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>
            Save school
          </Button>
        </Form>
      </Modal>
    </>
  );
}

function SubjectsWorkspace({
  subjects,
  topics,
  subjectTopicsCache,
  setSubjectTopicsCache,
  expandedRowKeys,
  setExpandedRowKeys,
  onCreateSubject,
  onUpdateSubject,
  onDeleteSubject,
  canManage
}: {
  subjects: Subject[];
  topics: Topic[];
  subjectTopicsCache: Record<string, Topic[]>;
  setSubjectTopicsCache: (cache: Record<string, Topic[]>) => void;
  expandedRowKeys: React.Key[];
  setExpandedRowKeys: (keys: React.Key[]) => void;
  onCreateSubject: (values: {
    name: string;
    description?: string;
  }) => Promise<void>;
  onUpdateSubject: (
    subjectId: string,
    patch: Partial<Omit<Subject, "id">>
  ) => Promise<void>;
  onDeleteSubject: (subjectId: string) => Promise<void>;
  canManage: boolean;
}) {
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const fetchTopicsForSubject = async (subjectId: string) => {
    if (subjectTopicsCache[subjectId]) return;
    try {
      const fetchedTopics = await fetchTopics(subjectId);
      setSubjectTopicsCache({ ...subjectTopicsCache, [subjectId]: fetchedTopics });
    } catch (err) {
      console.error("Failed to fetch topics:", err);
    }
  };

  const handleExpand = (expanded: boolean, record: Subject) => {
    setExpandedRowKeys(expanded
      ? [...expandedRowKeys, record.id]
      : expandedRowKeys.filter((k) => k !== record.id));
    if (expanded) {
      fetchTopicsForSubject(record.id);
    }
  };

  const columns: TableColumnsType<Subject> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: string) => (
        <Space>
          <BookOutlined style={{ color: "#1890ff" }} />
          <Text strong>{name}</Text>
        </Space>
      )
    },
    {
      title: "Topics",
      key: "topicCount",
      render: (_, record) => {
        const count = topics.filter(t => t.subjectId === record.id).length;
        return <Text>{count} topics</Text>;
      }
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) =>
        canManage ? (
          <Space>
            <Button size="small" onClick={() => {
              setEditingSubject(record);
              setIsEditModalOpen(true);
            }}>
              Edit
            </Button>
            <Button danger size="small" onClick={() => void onDeleteSubject(record.id)}>
              Delete
            </Button>
          </Space>
        ) : null
    }
  ];

  return (
    <>
      <Card
        title="Subjects"
        extra={
          <Button type="primary" onClick={() => setIsModalOpen(true)} disabled={!canManage}>
            Add Subject
          </Button>
        }
      >
        <Paragraph type="secondary">
          Subjects organize topics and lessons. Create subjects to organize your curriculum.
        </Paragraph>
        <Table 
          rowKey="id" 
          pagination={{ pageSize: 10 }} 
          columns={columns} 
          dataSource={subjects}
          expandable={{
            expandedRowKeys,
            expandedRowRender: (record) => {
              const subjectTopics = subjectTopicsCache[record.id] || [];
              if (subjectTopics.length === 0) {
                return <Text type="secondary">No topics in this subject</Text>;
              }
              return (
                <List
                  size="small"
                  dataSource={subjectTopics}
                  renderItem={(topic) => (
                    <List.Item style={{ padding: "8px 0" }}>
                      <Space>
                        <FileTextOutlined style={{ color: "#52c41a" }} />
                        <Text>{topic.name}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              );
            },
            onExpand: handleExpand,
            rowExpandable: (record) => true,
          }}
        />
      </Card>

      <Modal
        title="Add Subject"
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            await onCreateSubject(values);
            form.resetFields();
            setIsModalOpen(false);
          }}
        >
          <Form.Item name="name" label="Subject Name" rules={[{ required: true, message: "Enter subject name" }]}>
            <Input placeholder="e.g., English, Mathematics, Life Sciences" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Brief description of this subject" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>
            Create Subject
          </Button>
        </Form>
      </Modal>

      <Modal
        title="Edit Subject"
        open={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          setEditingSubject(null);
        }}
        footer={null}
      >
        {editingSubject && (
          <Form
            layout="vertical"
            initialValues={{
              name: editingSubject.name,
              description: editingSubject.description
            }}
            onFinish={async (values) => {
              await onUpdateSubject(editingSubject.id, values);
              setIsEditModalOpen(false);
              setEditingSubject(null);
            }}
          >
            <Form.Item name="name" label="Subject Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input.TextArea rows={2} />
            </Form.Item>
            <Button type="primary" htmlType="submit" block>
              Save Changes
            </Button>
          </Form>
        )}
      </Modal>
    </>
  );
}

function UnderstandingMapWorkspace({
  topics,
  subjects,
  intelligence,
  currentUser,
  recommendedNextStep,
  recommendedNextStepLoading,
  syncSelectedSubjectId,
  onSubjectChange,
  onGenerateTopicQuiz,
  onGenerateDiagnosticPractice
}: {
  topics: Topic[];
  subjects: Subject[];
  intelligence: IntelligenceState | null;
  currentUser: UserAccount | null;
  recommendedNextStep?: RecommendedNextStep | null;
  recommendedNextStepLoading?: boolean;
  syncSelectedSubjectId?: string;
  onSubjectChange?: (subjectId: string) => void;
  onGenerateTopicQuiz?: (subjectId: string, topicId: string) => Promise<void>;
  onGenerateDiagnosticPractice?: (subjectId: string, topicId: string, label?: string) => Promise<void>;
}) {
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const isStudent = currentUser?.role === "student";
  const [selectedSubjectId, setSelectedSubjectId] = useState<string>(
    isStudent && subjects.length > 0 ? subjects[0].id : ""
  );

  useEffect(() => {
    if (syncSelectedSubjectId && subjects.some((s) => s.id === syncSelectedSubjectId)) {
      setSelectedSubjectId(syncSelectedSubjectId);
    }
  }, [syncSelectedSubjectId, subjects]);

  const visibleTopics = isStudent
    ? selectedSubjectId
      ? topics.filter(t => t.subjectId === selectedSubjectId)
      : []
    : topics;

  useEffect(() => {
    if (!selectedTopicId) return;
    if (!visibleTopics.some((t) => t.id === selectedTopicId)) {
      setSelectedTopicId(null);
    }
  }, [selectedTopicId, visibleTopics]);

  const masteryData = intelligence 
    ? getMasteryVisualization(intelligence, visibleTopics)
    : visibleTopics.map(t => ({
        topicId: t.id,
        topicName: t.name,
        percent: 0,
        level: "no_data",
        color: "#d9d9d9",
        prerequisites: t.prerequisiteTopicIds || [],
        assessed: false
      }));

  const selectedTopic = visibleTopics.find(t => t.id === selectedTopicId);
  const selectedMastery = selectedTopicId && intelligence
    ? getTopicMastery(intelligence, selectedTopicId)
    : null;

  const timelineTopics = useMemo(() => {
    const byId = new Map(visibleTopics.map((t) => [t.id, t]));
    const memo = new Map<string, number>();
    const depth = (id: string, seen = new Set<string>()): number => {
      if (memo.has(id)) return memo.get(id) || 0;
      if (seen.has(id)) return 0;
      const nextSeen = new Set(seen);
      nextSeen.add(id);
      const topic = byId.get(id);
      if (!topic) return 0;
      const prereqs = (topic.prerequisiteTopicIds || []).filter((pid) => byId.has(pid));
      const d = prereqs.length ? 1 + Math.max(...prereqs.map((pid) => depth(pid, nextSeen))) : 0;
      memo.set(id, d);
      return d;
    };

    return visibleTopics
      .map((topic) => ({ topic, depth: depth(topic.id) }))
      .sort((a, b) => a.depth - b.depth || a.topic.name.localeCompare(b.topic.name));
  }, [visibleTopics]);

  const nodeMetrics = useMemo(() => {
    const map = new Map(masteryData.map((m) => [m.topicId, m]));
    return map;
  }, [masteryData]);

  const selectedTopicPrereqs = selectedTopic?.prerequisiteTopicIds || [];
  const selectedTopicDependents = visibleTopics.filter(t => 
    (t.prerequisiteTopicIds || []).includes(selectedTopicId || "")
  );
  const visibleRecommendedNextStep = recommendedNextStep?.subjectId === selectedSubjectId
    ? recommendedNextStep
    : null;

  const masteryTimelineLabel = (assessed: boolean, percent: number) => {
    if (!assessed) return "No data";
    if (percent < 50) return `${percent}% needs focus`;
    if (percent < 80) return `${percent}% some work`;
    return `${percent}% on track`;
  };

  return (
    <>
      {isStudent && subjects.length > 0 && (
        <Tabs
          activeKey={selectedSubjectId}
          onChange={(key) => {
            setSelectedSubjectId(key);
            setSelectedTopicId(null);
            onSubjectChange?.(key);
          }}
          items={subjects.map((subject) => ({
            key: subject.id,
            label: subject.name
          }))}
          style={{ marginBottom: 16 }}
        />
      )}
      {isStudent && (recommendedNextStepLoading || visibleRecommendedNextStep) ? (
        <Card
          key={visibleRecommendedNextStep?.subjectId || `loading-${selectedSubjectId}`}
          className="recommendation-card-transition"
          title="Recommended next step"
          style={{ marginBottom: 16, borderColor: brandTokens.colorPrimary }}
          extra={visibleRecommendedNextStep ? <Tag color="blue">{Math.round((visibleRecommendedNextStep.confidence || 0) * 100)}% confidence</Tag> : null}
        >
          {recommendedNextStepLoading && !visibleRecommendedNextStep ? (
            <div className="recommendation-loading-state">
              <Skeleton active title={{ width: "42%" }} paragraph={{ rows: 3, width: ["86%", "72%", "48%"] }} />
            </div>
          ) : visibleRecommendedNextStep ? (
          <Space direction="vertical" size={12} style={{ display: "flex" }}>
            <div>
              <Title level={4} style={{ margin: 0 }}>{visibleRecommendedNextStep.title}</Title>
              <Text type="secondary">{visibleRecommendedNextStep.reason}</Text>
            </div>
            <Alert
              type="info"
              showIcon
              message="Why this comes first"
              description={visibleRecommendedNextStep.evidence}
            />
            {onGenerateDiagnosticPractice ? (
              <Button
                type="primary"
                onClick={() => void onGenerateDiagnosticPractice(
                  visibleRecommendedNextStep.subjectId,
                  visibleRecommendedNextStep.topicId,
                  visibleRecommendedNextStep.conceptName || visibleRecommendedNextStep.topicName
                )}
              >
                Start 5 diagnostic questions
              </Button>
            ) : null}
          </Space>
          ) : null}
        </Card>
      ) : null}
      {isStudent && !recommendedNextStepLoading && !visibleRecommendedNextStep ? (
        <Card title="Recommended next step" style={{ marginBottom: 16 }}>
          <Empty description="Complete a short practice set or ask Kwazi a question to get a focused next step." />
        </Card>
      ) : null}
      {isStudent && !recommendedNextStepLoading && !visibleRecommendedNextStep && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16, background: "#f7fafc", border: "1px solid #e2e8f0" }}
          message={<Text strong>Not sure what to do next?</Text>}
          description={<Text>Open the Chat workspace and ask Kwazi a question about anything you find confusing. Kwazi will identify your gaps and give you tailored explanations to close them.</Text>}
        />
      )}
      {!isStudent ? (
      <Row gutter={[16, 16]}>
      <Col xs={24} lg={16}>
        <Card title="Subject topic map">
          {timelineTopics.length ? (
            <Timeline
              mode="left"
              items={timelineTopics.map(({ topic, depth }) => {
                const metric = nodeMetrics.get(topic.id);
                const assessed = Boolean(metric?.assessed);
                const pct = metric?.percent || 0;
                const isSelected = selectedTopicId === topic.id;
                const needsPractice = !assessed || pct < 80;
                const prereqNames = (topic.prerequisiteTopicIds || [])
                  .map((id) => visibleTopics.find((candidate) => candidate.id === id)?.name)
                  .filter(Boolean);
                return {
                  color: assessed ? getMasteryColor(pct) : "gray",
                  label: <Tag color={assessed ? getMasteryColor(pct) : "default"}>{masteryTimelineLabel(assessed, pct)}</Tag>,
                  children: (
                    <button
                      type="button"
                      onClick={() => setSelectedTopicId(isSelected ? null : topic.id)}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: isSelected ? `2px solid ${brandTokens.colorPrimary}` : `1px solid ${brandTokens.colorBorder}`,
                        borderRadius: 12,
                        padding: "12px 14px",
                        background: isSelected ? "rgba(22, 119, 255, 0.08)" : brandTokens.colorSurfaceAlt,
                        cursor: "pointer",
                      }}
                    >
                      <Space direction="vertical" size={8} style={{ display: "flex" }}>
                        <Space wrap align="start" style={{ justifyContent: "space-between", width: "100%" }}>
                          <Text strong>{topic.name}</Text>
                          {onGenerateTopicQuiz && selectedSubjectId ? (
                            <Button
                              size="small"
                              type={needsPractice ? "primary" : "default"}
                              onClick={(event) => {
                                event.stopPropagation();
                                void onGenerateTopicQuiz(selectedSubjectId, topic.id);
                              }}
                            >
                              {needsPractice ? "Practice" : "Improve"}
                            </Button>
                          ) : null}
                        </Space>
                        <Text type="secondary">{topic.description || "No description"}</Text>
                        {prereqNames.length ? (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Requires: {prereqNames.join(", ")}
                          </Text>
                        ) : (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Foundation topic
                          </Text>
                        )}
                      </Space>
                    </button>
                  ),
                };
              })}
            />
          ) : (
            <Empty description="No topics available for this subject." />
          )}

          {selectedTopic && (
            <div style={{ marginTop: 24, padding: 16, ...surfaceStyles.subtleCard }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div>
                  <Title level={4} style={{ margin: 0 }}>{selectedTopic.name}</Title>
                  <Text type="secondary">{selectedTopic.description || "No description"}</Text>
                </div>
                {selectedMastery?.params?.attempts ? (
                  <Space size={8} align="center">
                    <Tag color={getMasteryColor(selectedMastery.percent)} style={{ fontSize: 16, padding: "4px 12px" }}>
                      {`${selectedMastery.percent}% - ${getMasteryLabel(selectedMastery.percent)}`}
                    </Tag>
                    {selectedMastery.percent < 40 ? (
                      <Button
                        type="primary"
                        onClick={() => {
                          if (!selectedTopicId || !selectedSubjectId || !onGenerateTopicQuiz) return;
                          void onGenerateTopicQuiz(selectedSubjectId, selectedTopicId);
                        }}
                      >
                        Practice this topic
                      </Button>
                    ) : null}
                  </Space>
                ) : (
                  <Button
                    type="primary"
                    onClick={() => {
                      if (!selectedTopicId || !selectedSubjectId || !onGenerateTopicQuiz) return;
                      void onGenerateTopicQuiz(selectedSubjectId, selectedTopicId);
                    }}
                  >
                    Practice this topic
                  </Button>
                )}
              </div>

              {selectedTopicPrereqs.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <Text strong style={{ display: "block", marginBottom: 8 }}>
                    <ArrowUpOutlined style={{ marginRight: 8 }} />
                    Prerequisites (you need these first):
                  </Text>
                  <Space wrap>
                    {selectedTopicPrereqs.map(prereqId => {
                      const prereq = topics.find(t => t.id === prereqId);
                      const prereqMastery = intelligence?.masteryState.topicMastery[prereqId];
                      return (
                        <Tag 
                          key={prereqId} 
                          color={prereqMastery ? getMasteryColor(Math.round(prereqMastery.pKnown * 100)) : "default"}
                          style={{ cursor: "pointer" }}
                          onClick={() => setSelectedTopicId(prereqId)}
                        >
                          {prereq?.name || prereqId} ({Math.round((prereqMastery?.pKnown || 0.3) * 100)}%)
                        </Tag>
                      );
                    })}
                  </Space>
                </div>
              )}

              {selectedTopicDependents.length > 0 && (
                <div>
                  <Text strong style={{ display: "block", marginBottom: 8 }}>
                    <ArrowDownOutlined style={{ marginRight: 8 }} />
                    Unlocks (these need you first):
                  </Text>
                  <Space wrap>
                    {selectedTopicDependents.map(dep => (
                      <Tag 
                        key={dep.id} 
                        color="blue"
                        style={{ cursor: "pointer" }}
                        onClick={() => setSelectedTopicId(dep.id)}
                      >
                        {dep.name}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}
            </div>
          )}
        </Card>
      </Col>

      <Col xs={24} lg={8}>
        <Card title="Learning Path">
          {selectedTopic ? (
            <List
              size="small"
              dataSource={[
                `Practice ${selectedTopic.name}`,
                ...selectedTopicPrereqs.map((id) => `Revisit prerequisite: ${visibleTopics.find((t) => t.id === id)?.name || id}`),
                ...selectedTopicDependents.slice(0, 3).map((dep) => `Then unlock: ${dep.name}`)
              ]}
              renderItem={(item, index) => (
                <List.Item>
                  <Space>
                    <Tag color={index === 0 ? "processing" : "default"}>Step {index + 1}</Tag>
                    <Text>{item}</Text>
                  </Space>
                </List.Item>
              )}
            />
          ) : (
            <Empty description="Select a topic to see a suggested path." />
          )}
        </Card>
      </Col>

    </Row>
      ) : null}
    </>
  );
}

function PracticeWorkspace({
  questions,
  answers,
  submitting,
  generating,
  quizSessionActive,
  subjects,
  currentUser,
  intelligence,
  understanding,
  sessionSummary,
  allTopics,
  syncSelectedSubjectId,
  onAnswerChange,
  onSubmitQuiz,
  onGenerateQuiz,
  onResumeQuiz,
  onExitQuiz,
  onSubjectChange: externalOnSubjectChange,
  quizHistory: externalQuizHistory,
}: {
  questions: QuizQuestion[];
  answers: Record<string, string>;
  submitting: boolean;
  generating: boolean;
  quizSessionActive: boolean;
  subjects: Subject[];
  currentUser: UserAccount | null;
  intelligence: IntelligenceState | null;
  understanding: StudentUnderstandingProfile | null;
  sessionSummary: EndOfSessionSummary | null;
  allTopics: Topic[];
  /** Keeps practice subject tabs aligned with app-level subject (e.g. chat). */
  syncSelectedSubjectId?: string;
  onAnswerChange: (questionId: string, value: string) => void;
  onSubmitQuiz: () => Promise<void>;
  onGenerateQuiz: (topicIds?: string[]) => Promise<void>;
  onResumeQuiz: (quizId: string) => Promise<void>;
  onExitQuiz: () => void;
  onSubjectChange?: (subjectId: string) => void;
  quizHistory?: any[];
}) {
  const isStudent = currentUser?.role === "student";
  const [selectedSubjectId, setSelectedSubjectId] = useState<string>(
    isStudent && subjects.length > 0 ? subjects[0].id : ""
  );
  const [selectedQuizTopicIds, setSelectedQuizTopicIds] = useState<string[]>([]);
  const hasSelectedSubject = Boolean(
    selectedSubjectId && subjects.some((subject) => subject.id === selectedSubjectId)
  );

  useEffect(() => {
    if (isStudent && subjects.length === 0) {
      setSelectedSubjectId("");
      return;
    }
    if (!subjects.some((s) => s.id === selectedSubjectId)) {
      const fallbackSubjectId = subjects[0]?.id || "";
      setSelectedSubjectId(fallbackSubjectId);
      if (fallbackSubjectId) {
        externalOnSubjectChange?.(fallbackSubjectId);
      }
      return;
    }
    if (
      syncSelectedSubjectId &&
      subjects.some((s) => s.id === syncSelectedSubjectId)
    ) {
      setSelectedSubjectId(syncSelectedSubjectId);
    }
  }, [externalOnSubjectChange, isStudent, selectedSubjectId, syncSelectedSubjectId, subjects]);

  const onSubjectChange = (key: string) => {
    setSelectedSubjectId(key);
    setSelectedQuizTopicIds([]);
    externalOnSubjectChange?.(key);
  };

  const answeredCount = questions.filter((question) => answers[question.id]).length;

  const subjectTopics = allTopics.filter((t) => t.subjectId === selectedSubjectId);
  const validSelectedQuizTopicIds = selectedQuizTopicIds.filter((id) => subjectTopics.some((topic) => topic.id === id));
  const selectedSubjectName = subjects.find(s => s.id === selectedSubjectId)?.name || "Practice";
  const quizHistoryForSubject = (externalQuizHistory || [])
    .filter((q: any) => q.subjectId === selectedSubjectId)
    .slice(0, 10);
  const activeQuiz = quizHistoryForSubject.find((q: any) => q.status === "pending" || q.status === "active");
  const lastCompletedQuiz = quizHistoryForSubject.find((q: any) => q.status === "completed");
  const combinedGapSignals = [...(intelligence?.gapSignals ?? []), ...(understanding?.gapSignals ?? [])]
    .filter((g, i, arr) => arr.findIndex((x) => x.topicId === g.topicId) === i);
  const recommendedTopicName = combinedGapSignals[0]?.topicName || subjectTopics[0]?.name || selectedSubjectName;
  const topicNameById = new Map(allTopics.map((topic) => [topic.id, topic.name]));
  const lastCompletedAnsweredCount = lastCompletedQuiz
    ? Math.max(0, Math.min(lastCompletedQuiz.answeredCount || 0, lastCompletedQuiz.questionCount || lastCompletedQuiz.answeredCount || 0))
    : 0;
  const lastCompletedCorrectCount = lastCompletedQuiz
    ? Math.max(0, Math.min(lastCompletedQuiz.correctCount || 0, lastCompletedAnsweredCount))
    : 0;
  const lastCompletedPercent = lastCompletedAnsweredCount > 0
    ? clampPercent(Math.round((lastCompletedCorrectCount / lastCompletedAnsweredCount) * 100))
    : null;
  const activeQuizPercent = activeQuiz?.questionCount > 0
    ? clampPercent(Math.round(((activeQuiz.answeredCount || 0) / activeQuiz.questionCount) * 100))
    : 0;

  const isColdStart = subjectTopics.every((t) => {
    const m = intelligence?.masteryState?.topicMastery[t.id];
    return !m || m.attempts === 0;
  });

  return (
    <>
      {isStudent && subjects.length > 0 && (
        <Tabs
          activeKey={selectedSubjectId}
          onChange={onSubjectChange}
          items={subjects.map((subject) => ({
            key: subject.id,
            label: subject.name
          }))}
          style={{ marginBottom: 16 }}
        />
      )}

      {quizSessionActive && questions.length > 0 ? (
        <Card title="Adaptive practice quiz">
          <TypeformQuiz
            questions={questions}
            answers={answers}
            submitting={submitting}
            onAnswer={onAnswerChange}
            onSubmit={onSubmitQuiz}
            onExit={onExitQuiz}
          />
        </Card>
      ) : (
        <Card title="Practice quiz">
          <Space direction="vertical" size={16} style={{ display: "flex" }}>
            {sessionSummary && sessionSummary.subjectId === selectedSubjectId ? (
              <Alert
                type="success"
                showIcon
                message={`Session summary: ${sessionSummary.score}/${sessionSummary.total} correct`}
                description={
                  <Space direction="vertical" size={4} style={{ display: "flex" }}>
                    {sessionSummary.strengthenedTopics.length > 0 ? (
                      <Text>Improved: {sessionSummary.strengthenedTopics.join(", ")}</Text>
                    ) : null}
                    {sessionSummary.needsAttentionTopics.length > 0 ? (
                      <Text>Needs attention: {sessionSummary.needsAttentionTopics.join(", ")}</Text>
                    ) : null}
                    <Text>
                      Next: {sessionSummary.nextStepTitle || "Kwazi will use this result to choose your next practice step."}
                    </Text>
                    {sessionSummary.nextStepReason ? <Text type="secondary">{sessionSummary.nextStepReason}</Text> : null}
                  </Space>
                }
              />
            ) : null}

            {!hasSelectedSubject ? (
              <Alert
                type="info"
                showIcon
                message="No subjects enrolled yet"
                description="Enroll in a subject before starting a practice quiz."
              />
            ) : null}

            <Row gutter={[16, 16]}>
              <Col xs={24} lg={15}>
                <Card
                  bordered
                  style={{
                    minHeight: 280,
                    borderColor: "#bfe3df",
                    background: "linear-gradient(135deg, #ffffff 0%, #f8fcfd 62%, #e8f6f4 100%)",
                  }}
                >
                  <Space direction="vertical" size={18} style={{ display: "flex" }}>
                    <Space wrap>
                      <Tag color={isColdStart ? "blue" : combinedGapSignals.length ? "orange" : "green"}>
                        {isColdStart ? "First quiz" : combinedGapSignals.length ? "Recommended next" : "Breadth check"}
                      </Tag>
                      <Tag color="processing">{selectedSubjectName}</Tag>
                    </Space>
                    <div>
                      <Title level={3} style={{ margin: 0 }}>
                        {isColdStart
                          ? "Start with one focused topic"
                          : combinedGapSignals.length
                            ? `Check ${recommendedTopicName} before the next quiz`
                            : "Build a clearer picture of your understanding"}
                      </Title>
                      <Text type="secondary">
                        {isColdStart
                          ? "Choose a topic and Kwazi will create a short diagnostic set to establish your starting point."
                          : combinedGapSignals.length
                            ? "Kwazi will use recent gaps and prerequisite signals to shape a short diagnostic practice set."
                            : "No major gaps are active yet, so this quiz will sample across topics and refine your mastery map."}
                      </Text>
                    </div>
                    <Row gutter={[12, 12]}>
                      <Col xs={24} sm={8}>
                        <Card size="small" style={{ height: "100%", borderRadius: brandTokens.radiusSm }}>
                          <Space align="center" size={12}>
                            <Progress
                              type="circle"
                              percent={combinedGapSignals.length ? Math.min(100, combinedGapSignals.length * 20) : Math.min(100, subjectTopics.length * 8)}
                              size={58}
                              strokeColor={combinedGapSignals.length ? brandTokens.colorWarning : brandTokens.colorPrimary}
                              format={() => `${combinedGapSignals.length || subjectTopics.length}`}
                            />
                            <div>
                              <Text strong>{combinedGapSignals.length ? "Gap signals" : "Topics"}</Text>
                              <br />
                              <Text type="secondary" style={{ fontSize: 12 }}>shape the quiz</Text>
                            </div>
                          </Space>
                        </Card>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Card size="small" style={{ height: "100%", borderRadius: brandTokens.radiusSm }}>
                          <Space align="center" size={12}>
                            <Progress
                              type="circle"
                              percent={lastCompletedPercent ?? 0}
                              size={58}
                              strokeColor={lastCompletedPercent == null ? brandTokens.colorBorder : brandTokens.colorSuccess}
                              format={() => lastCompletedPercent == null ? "New" : `${lastCompletedPercent}%`}
                            />
                            <div>
                              <Text strong>Last result</Text>
                              <br />
                              <Text type="secondary" style={{ fontSize: 12 }}>{lastCompletedQuiz ? "from previous quiz" : "no quiz yet"}</Text>
                            </div>
                          </Space>
                        </Card>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Card size="small" style={{ height: "100%", borderRadius: brandTokens.radiusSm }}>
                          <Space align="center" size={12}>
                            <Progress
                              type="circle"
                              percent={100}
                              size={58}
                              strokeColor={brandTokens.colorPrimary}
                              format={() => "5m"}
                            />
                            <div>
                              <Text strong>Quick check</Text>
                              <br />
                              <Text type="secondary" style={{ fontSize: 12 }}>short diagnostic</Text>
                            </div>
                          </Space>
                        </Card>
                      </Col>
                    </Row>
                    <Space direction="vertical" size={12} style={{ display: "flex", width: "100%" }}>
                      <Select
                        mode="multiple"
                        allowClear
                        placeholder="All topics"
                        value={validSelectedQuizTopicIds}
                        disabled={generating || !hasSelectedSubject}
                        maxTagCount="responsive"
                        style={{ width: "100%" }}
                        options={subjectTopics.map((topic) => ({ value: topic.id, label: topic.name }))}
                        onChange={setSelectedQuizTopicIds}
                      />
                      <Button
                        type="primary"
                        icon={<ThunderboltOutlined />}
                        loading={generating}
                        disabled={!hasSelectedSubject}
                        onClick={() => void onGenerateQuiz(validSelectedQuizTopicIds.length ? validSelectedQuizTopicIds : undefined)}
                      >
                        <Space>
                          Start personalized quiz
                          <ClusterOutlined />
                        </Space>
                      </Button>
                    </Space>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} lg={9}>
                <Space direction="vertical" size={16} style={{ display: "flex" }}>
                  <Card size="small" title="What shapes this quiz">
                    {combinedGapSignals.length ? (
                      <Space wrap>
                        {combinedGapSignals.slice(0, 5).map((g) => (
                          <Tag key={g.id} color="orange">
                            {g.topicName}
                          </Tag>
                        ))}
                      </Space>
                    ) : (
                      <Text type="secondary">Breadth across enrolled topics. Complete a quiz to refine gap detection.</Text>
                    )}
                  </Card>
                  <Card size="small" title="Last quiz">
                    {lastCompletedQuiz ? (
                      <Space align="center" size={16} style={{ width: "100%", justifyContent: "space-between" }}>
                        <div>
                          <Text strong>{lastCompletedCorrectCount}/{lastCompletedAnsweredCount} correct</Text>
                          <br />
                          <Text type="secondary">{lastCompletedQuiz.createdAt ? new Date(lastCompletedQuiz.createdAt).toLocaleDateString() : "Completed"}</Text>
                        </div>
                        <Progress
                          type="circle"
                          percent={lastCompletedPercent || 0}
                          size={64}
                          strokeColor={brandTokens.colorSuccess}
                          format={() => `${lastCompletedPercent}%`}
                        />
                      </Space>
                    ) : (
                      <Text type="secondary">No completed quiz yet.</Text>
                    )}
                  </Card>
                  {activeQuiz ? (
                    <Card size="small" title="In progress">
                      <Space align="center" size={16} style={{ width: "100%", justifyContent: "space-between" }}>
                        <Progress
                          type="circle"
                          percent={activeQuizPercent}
                          size={64}
                          strokeColor={brandTokens.colorPrimary}
                          format={() => `${activeQuiz.answeredCount || 0}/${activeQuiz.questionCount || "?"}`}
                        />
                        <div style={{ flex: 1 }}>
                          <Text strong>Resume your quiz</Text>
                          <br />
                          <Text type="secondary">{activeQuizPercent}% complete</Text>
                        </div>
                        <Button size="small" type="link" onClick={() => onResumeQuiz(activeQuiz.id)}>
                          Continue
                        </Button>
                      </Space>
                    </Card>
                  ) : null}
                </Space>
              </Col>
            </Row>
          </Space>

          <Collapse
            style={{ marginTop: 24 }}
            defaultActiveKey={["previous-quizzes"]}
            items={[
              {
                key: "previous-quizzes",
                label: <Title level={5} style={{ margin: 0 }}>Previous quizzes — {selectedSubjectName}</Title>,
                children: quizHistoryForSubject.length ? (
                  <Row gutter={[12, 12]}>
                    {quizHistoryForSubject.map((quiz: any) => {
                      const inProgress = quiz.status === "pending" || quiz.status === "active";
                      const displayedAnsweredCount = Math.max(0, Math.min(quiz.answeredCount || 0, quiz.questionCount || quiz.answeredCount || 0));
                      const displayedCorrectCount = Math.max(0, Math.min(quiz.correctCount || 0, displayedAnsweredCount));
                      const percent = displayedAnsweredCount > 0 ? clampPercent(Math.round((displayedCorrectCount / displayedAnsweredCount) * 100)) : null;
                      const progressPercent = quiz.questionCount > 0 ? clampPercent(Math.round((displayedAnsweredCount / quiz.questionCount) * 100)) : 0;
                      const label = quiz.status === "completed" ? "completed" : quiz.status === "active" ? "in progress" : "incomplete";
                      const color = quiz.status === "completed" ? "green" : quiz.status === "active" ? "blue" : "orange";
                      return (
                        <Col xs={24} md={12} xl={8} key={quiz.id}>
                          <Card size="small" style={{ height: "100%" }}>
                            <Space align="start" size={14} style={{ width: "100%" }}>
                              <Progress
                                type="circle"
                                percent={percent ?? progressPercent}
                                size={72}
                                strokeColor={quiz.status === "completed" ? brandTokens.colorSuccess : inProgress ? brandTokens.colorPrimary : brandTokens.colorWarning}
                                format={() => percent == null ? `${displayedAnsweredCount}/${quiz.questionCount || "?"}` : `${percent}%`}
                              />
                              <Space direction="vertical" size={8} style={{ flex: 1, minWidth: 0 }}>
                                <Space style={{ justifyContent: "space-between", width: "100%" }}>
                                  <Tag color={color}>{label}</Tag>
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    {quiz.createdAt ? new Date(quiz.createdAt).toLocaleDateString() : "-"}
                                  </Text>
                                </Space>
                                <Title level={5} style={{ margin: 0 }}>
                                  {percent == null ? `${quiz.questionCount || 0} question quiz` : `${displayedCorrectCount}/${displayedAnsweredCount} correct`}
                                </Title>
                                <Text type="secondary">
                                  {percent == null ? `${progressPercent}% complete` : "Completed practice set"}
                                </Text>
                                {quiz.topicIds?.length ? (
                                  <Space wrap size={[4, 4]}>
                                    {quiz.topicIds.slice(0, 3).map((id: string) => (
                                      <Tag key={id}>{topicNameById.get(id) || id.replace(/^topic-/, '').replace(/-/g, ' ')}</Tag>
                                    ))}
                                  </Space>
                                ) : null}
                                {inProgress ? (
                                  <Button size="small" type="link" onClick={() => onResumeQuiz(quiz.id)} style={{ paddingInline: 0, alignSelf: "flex-start" }}>
                                    {quiz.status === "active" ? "Continue quiz" : "Complete quiz"}
                                  </Button>
                                ) : null}
                              </Space>
                            </Space>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No quizzes yet" />
                ),
              },
            ]}
          />

        </Card>
      )}
    </>
  );
}

function ChatWorkspace({
  chatHistory,
  chatSessions,
  activeChatSessionId,
  chatSessionsLoading,
  submitting,
  subjects,
  currentUser,
  syncSelectedSubjectId,
  onSendChat,
  onCreateChatSession,
  onSelectChatSession,
  onRenameChatSession,
  onDeleteChatSession,
  onSubjectChange: externalOnSubjectChange
}: {
  chatHistory: ChatMessage[];
  chatSessions: StudentChatSession[];
  activeChatSessionId: string | null;
  chatSessionsLoading: boolean;
  submitting: boolean;
  subjects: Subject[];
  currentUser: UserAccount | null;
  syncSelectedSubjectId?: string;
  onSendChat: (message: string, subjectId?: string, chatSessionId?: string) => Promise<void>;
  onCreateChatSession: (subjectId?: string) => Promise<void>;
  onSelectChatSession: (chatSessionId: string) => Promise<void>;
  onRenameChatSession: (chatSessionId: string, title: string, subjectId?: string) => Promise<void>;
  onDeleteChatSession: (chatSessionId: string, subjectId?: string) => Promise<void>;
  onSubjectChange?: (subjectId: string) => void;
}) {
  const isStudent = currentUser?.role === "student";
  const [selectedSubjectId, setSelectedSubjectId] = useState<string>(
    isStudent && subjects.length > 0 ? subjects[0].id : ""
  );
  const hasSelectedSubject = Boolean(
    selectedSubjectId && subjects.some((subject) => subject.id === selectedSubjectId)
  );

  useEffect(() => {
    if (isStudent && subjects.length === 0) {
      setSelectedSubjectId("");
      return;
    }
    if (!subjects.some((s) => s.id === selectedSubjectId)) {
      const fallbackSubjectId = subjects[0]?.id || "";
      setSelectedSubjectId(fallbackSubjectId);
      if (fallbackSubjectId) {
        externalOnSubjectChange?.(fallbackSubjectId);
      }
      return;
    }
    if (
      syncSelectedSubjectId &&
      subjects.some((s) => s.id === syncSelectedSubjectId)
    ) {
      setSelectedSubjectId(syncSelectedSubjectId);
    }
  }, [externalOnSubjectChange, isStudent, selectedSubjectId, syncSelectedSubjectId, subjects]);

  const onSubjectChange = (key: string) => {
    setSelectedSubjectId(key);
    externalOnSubjectChange?.(key);
  };
  const [draft, setDraft] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory.length]);

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  const [drawerOpen, setDrawerOpen] = useState(false);

  const formatSessionDate = (dateStr?: string) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString([], { month: "short", day: "numeric" });
    } catch {
      return "";
    }
  };

  const sessionsList = chatSessionsLoading ? (
    <div style={{ display: "grid", placeItems: "center", minHeight: 160 }}>
      <Spin />
    </div>
  ) : chatSessions.length ? (
    <List
      dataSource={chatSessions}
      renderItem={(session) => {
        const active = session.id === activeChatSessionId;
        return (
          <List.Item
            onClick={() => { setDrawerOpen(false); void onSelectChatSession(session.id); }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setDrawerOpen(false);
                void onSelectChatSession(session.id);
              }
            }}
            role="button"
            tabIndex={0}
            style={{
              cursor: "pointer",
              borderRadius: 10,
              marginBottom: 8,
              padding: "10px 12px",
              border: active ? `1px solid ${brandTokens.colorPrimary}` : `1px solid ${brandTokens.colorBorder}`,
              background: active ? "rgba(22, 119, 255, 0.08)" : brandTokens.colorSurfaceAlt,
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, width: "100%" }}>
              <Space direction="vertical" size={2} style={{ flex: 1, minWidth: 0 }}>
                <Text
                  strong
                  ellipsis
                  editable={{
                    tooltip: "Rename chat",
                    triggerType: ["icon"],
                    onStart: (event) => event?.stopPropagation(),
                    onChange: (value) => {
                      const nextTitle = value.trim();
                      if (!nextTitle || nextTitle === session.title) return;
                      void onRenameChatSession(session.id, nextTitle, selectedSubjectId).catch((error) => {
                        message.error(`Failed to rename chat. ${getErrorMessage(error)}`);
                      });
                    },
                  }}
                >
                  {session.title || "New chat"}
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {formatSessionDate(session.lastMessageAt || session.createdAt)}
                </Text>
              </Space>
              <Popconfirm
                title="Delete this chat?"
                description="This removes it from your previous chats."
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
                onPopupClick={(event) => event.stopPropagation()}
                onConfirm={(event) => {
                  event?.stopPropagation();
                  void onDeleteChatSession(session.id, selectedSubjectId).catch((error) => {
                    message.error(`Failed to delete chat. ${getErrorMessage(error)}`);
                  });
                }}
              >
                <Tooltip
                  title="Delete chat"
                  placement="top"
                  overlayInnerStyle={{
                    alignItems: "center",
                    background: brandTokens.colorHeading,
                    borderRadius: 8,
                    display: "inline-flex",
                    lineHeight: 1.15,
                    minHeight: 0,
                    padding: "4px 6px",
                    textAlign: "initial",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Button
                    aria-label={`Delete ${session.title || "chat"}`}
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    type="text"
                    disabled={submitting}
                    onClick={(event) => event.stopPropagation()}
                  />
                </Tooltip>
              </Popconfirm>
            </div>
          </List.Item>
        );
      }}
    />
  ) : (
    <Empty description="No previous chats" />
  );

  return (
    <>
      {isStudent && subjects.length > 0 && (
        <Tabs
          activeKey={selectedSubjectId}
          onChange={onSubjectChange}
          items={subjects.map((subject) => ({
            key: subject.id,
            label: subject.name
          }))}
          style={{ marginBottom: 16 }}
        />
      )}
      <Card
        title="Chat-driven gap inference"
        extra={
          <Tag color="processing">Ask what you do not yet understand</Tag>
        }
      >
      <div style={{ position: "relative" }}>
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Space direction="vertical" size={16} style={{ display: "flex" }}>
              <div
                style={{
                  minHeight: 360,
                  maxHeight: 440,
                  overflowY: "auto",
                  padding: "8px 0"
                }}
              >
                {!chatHistory.length ? (
                  <Empty description={isStudent && !hasSelectedSubject ? "No subjects enrolled yet." : "No chat history yet."} />
                ) : (
                  <List
                    dataSource={chatHistory}
                    renderItem={(message) => (
                      (() => {
                        const isPendingAssistant =
                          message.role === "assistant" && message.id.startsWith("pending-assistant-");
                        const isUserMessage = message.role === "user";
                        const displayedContent = isUserMessage
                          ? truncateChatContent(message.content, USER_CHAT_DISPLAY_CHARACTER_LIMIT)
                          : message.content;
                        return (
                      <List.Item
                        style={{
                          justifyContent: message.role === "user" ? "flex-end" : "flex-start",
                          minWidth: 0,
                          width: "100%"
                        }}
                      >
                        <div
                          className={`chat-option-b-message-block${isUserMessage ? " chat-option-b-message-block--user" : ""}`}
                        >
                          <div className="chat-option-b-label">{isUserMessage ? "You" : "Tutor"}</div>
                          <div className={`chat-option-b-bubble${isUserMessage ? " chat-option-b-bubble--user" : ""}`}>
                            {isPendingAssistant ? (
                              <Space size={8} align="center">
                                <Spin size="small" />
                                <Text style={{ color: brandTokens.colorInk }}>Thinking...</Text>
                              </Space>
                            ) : null}
                            {!isPendingAssistant ? (
                              <>
                                <FormattedChatMessage
                                  content={displayedContent}
                                  color={isUserMessage ? "#124f49" : "#102a43"}
                                />
                              </>
                            ) : null}
                            {message.citations?.length ? (
                              <Space wrap style={{ minWidth: 0 }}>
                                {message.citations.map((citation) => (
                                  <Tag key={citation} color="green">
                                    {citation}
                                  </Tag>
                                ))}
                              </Space>
                            ) : null}
                            {message.signals?.length ? (
                              <Space wrap style={{ minWidth: 0 }}>
                                {message.signals.map((signal) => (
                                  <Tag key={signal.id} color={getSeverityTagColor(signal.severity)}>
                                    {signal.topicName} via {signal.signalSource}
                                  </Tag>
                                ))}
                              </Space>
                            ) : null}
                          </div>
                          {!isUserMessage ? (
                            <div className="chat-option-b-meta">{formatTime(message.createdAt)}</div>
                          ) : null}
                        </div>
                      </List.Item>
                        );
                      })()
                    )}
                  />
                )}
                <div ref={messagesEndRef} />
              </div>
              <Input.TextArea
                rows={3}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={hasSelectedSubject ? "Ask about the part of the lesson you still do not understand." : "Enroll in a subject before starting a chat."}
                disabled={submitting || !hasSelectedSubject}
                onPressEnter={(e) => {
                  if (!e.shiftKey && draft.trim()) {
                    e.preventDefault();
                    if (!submitting && hasSelectedSubject && draft.trim()) {
                      const next = draft.trim();
                      setDraft("");
                      void onSendChat(next, selectedSubjectId, activeChatSessionId || undefined);
                    }
                  }
                }}
              />
              <Button
                type="primary"
                loading={submitting}
                disabled={!draft.trim() || !hasSelectedSubject}
                onClick={async () => {
                  const next = draft.trim();
                  setDraft("");
                  await onSendChat(next, selectedSubjectId, activeChatSessionId || undefined);
                }}
              >
                Send question
              </Button>
            </Space>
          </Col>
        </Row>
        {chatSessions.length > 0 ? (
          <Button
            type="default"
            shape="circle"
            icon={<MenuOutlined />}
            size="large"
            style={{
              position: "absolute",
              right: -12,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              borderColor: brandTokens.colorBorder,
            }}
            onClick={() => setDrawerOpen(true)}
          />
        ) : null}
      </div>
      <Drawer
        title="Previous chats"
        placement="right"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={320}
        extra={
          <Button
            type="primary"
            size="small"
            icon={<FormOutlined />}
            onClick={() => { setDrawerOpen(false); void onCreateChatSession(selectedSubjectId); }}
            disabled={!selectedSubjectId || submitting}
          >
            New chat
          </Button>
        }
      >
        {sessionsList}
      </Drawer>
    </Card>
    </>
  );
}

function workspaceIcon(key: WorkspaceKey) {
  if (key === "overview") {
    return <BookOutlined />;
  }

  if (key === "profile") {
    return <SafetyCertificateOutlined />;
  }

  if (key === "schools") {
    return <BankOutlined />;
  }

  if (key === "materials") {
    return <ReadOutlined />;
  }

  if (key === "topic-graph" || key === "understanding-map") {
    return <ClusterOutlined />;
  }

  if (key === "practice") {
    return <SafetyCertificateOutlined />;
  }

  if (key === "chat") {
    return <MessageOutlined />;
  }

  if (key === "accounts") {
    return <TeamOutlined />;
  }

  return <ApiOutlined />;
}

function roleTagColor(role?: UserAccount["role"]) {
  if (role === "superadmin") {
    return "magenta";
  }

  return "green";
}

function ingestionColor(status?: StudyMaterial["ingestionStatus"]) {
  if (status === "failed") {
    return "red";
  }

  if (status === "processing" || status === "queued") {
    return "gold";
  }

  return "green";
}

function renderChatMath(expression: string, displayMode = false): { __html: string } | null {
  try {
    return {
      __html: katex.renderToString(expression, {
        displayMode,
        throwOnError: false,
        strict: "ignore",
        trust: false
      })
    };
  } catch {
    return null;
  }
}

function isChatMathFragmentLine(line: string) {
  const trimmed = line.trim();
  return Boolean(trimmed) && trimmed.length <= 18 && /^[A-Za-z0-9+\-−=()^*/.\s]+$/.test(trimmed);
}

function formatChatMathFragments(fragments: string[]) {
  const tokens = fragments.flatMap((fragment) => fragment.trim().split(/\s+/)).filter(Boolean);
  const midpoint = tokens.length / 2;
  if (Number.isInteger(midpoint) && midpoint > 0) {
    const left = tokens.slice(0, midpoint).join("");
    const right = tokens.slice(midpoint).join("");
    if (left === right) tokens.splice(midpoint);
  }

  return tokens.join(" ")
    .replace(/−/g, "-")
    .replace(/\b([A-Za-z])\s+([0-9]+)\b/g, "$1^$2")
    .replace(/\b([0-9]+)\s+([A-Za-z])\s+([A-Za-z])\b/g, "$1$2$3")
    .replace(/\b([0-9]+)\s+([A-Za-z])\b/g, "$1$2")
    .replace(/\b([A-Za-z])\s+([A-Za-z])\b/g, "$1$2")
    .replace(/\s*([=+\-*/])\s*/g, " $1 ")
    .replace(/\s+/g, " ")
    .trim();
}

function joinChatMathFragments(lines: string[]) {
  const normalized: string[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    if (!isChatMathFragmentLine(lines[index])) {
      normalized.push(lines[index]);
      continue;
    }

    const fragments: string[] = [];
    let cursor = index;
    while (cursor < lines.length && isChatMathFragmentLine(lines[cursor])) {
      fragments.push(lines[cursor].trim());
      cursor += 1;
    }

    if (fragments.length >= 2) {
      const inlineMath = `\\(${formatChatMathFragments(fragments)}\\)`;
      const previous = normalized[normalized.length - 1];
      if (previous?.trim() && !previous.trim().endsWith(":")) {
        normalized[normalized.length - 1] = `${previous.trimEnd()} ${inlineMath}`;
      } else {
        normalized.push(inlineMath);
      }

      const nextLine = lines[cursor]?.trim();
      if (nextLine && /^(for|within|in|of|to|from|with)\b/i.test(nextLine)) {
        normalized[normalized.length - 1] = `${normalized[normalized.length - 1].trimEnd()} ${nextLine}`;
        cursor += 1;
      }
      index = cursor - 1;
    } else {
      normalized.push(...lines.slice(index, cursor));
      index = cursor - 1;
    }
  }

  return normalized.join("\n");
}

function renderInlineChatMarkdown(text: string, color: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\\\([\s\S]*?\\\)|\\\[[\s\S]*?\\\])/g);
  return parts.filter(Boolean).map((part, index) => {
    const key = `${part}-${index}`;
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("*") && part.endsWith("*")) {
      return <em key={key}>{part.slice(1, -1)}</em>;
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <code
          key={key}
          style={{
            background: "rgba(15, 23, 42, 0.08)",
            borderRadius: 4,
            padding: "1px 4px",
            color,
            overflowWrap: "break-word",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word"
          }}
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    if ((part.startsWith("\\(") && part.endsWith("\\)")) || (part.startsWith("\\[") && part.endsWith("\\]"))) {
      const displayMode = part.startsWith("\\[");
      const math = renderChatMath(part.slice(2, -2), displayMode);
      if (!math) {
        return <code key={key} style={{ color }}>{part}</code>;
      }
      return (
        <span
          key={key}
          className={displayMode ? "chat-math chat-math--block" : "chat-math"}
          style={{ color, maxWidth: "100%", overflowX: "auto" }}
          dangerouslySetInnerHTML={math}
        />
      );
    }
    return part;
  });
}

const USER_CHAT_DISPLAY_CHARACTER_LIMIT = 360;

function normalizeChatMarkdown(content: unknown) {
  const visibleContent = extractVisibleChatContent(content)
    .replace(/\\\[\s*([\s\S]*?)\s*\\\]/g, (_match, expression) => `\n\\[${String(expression).replace(/\s+/g, " ").trim()}\\]\n`)
    .replace(/\\\(\s*([\s\S]*?)\s*\\\)/g, (_match, expression) => `\\(${String(expression).replace(/\s+/g, " ").trim()}\\)`);

  return joinChatMathFragments(visibleContent.split(/\r?\n/))
    .replace(/\s+(#{1,6}\s+)/g, "\n\n$1")
    .replace(/\s+(\d+\.\s+)/g, "\n$1")
    .trim();
}

function truncateChatContent(content: unknown, characterLimit: number) {
  const visibleContent = extractVisibleChatContent(content).trim();
  if (visibleContent.length <= characterLimit) return visibleContent;
  return `${visibleContent.slice(0, characterLimit).trimEnd()}...`;
}

function extractVisibleChatContent(content: unknown) {
  if (typeof content !== "string") return String(content ?? "");
  const trimmed = content.trim();

  try {
    const parsed = JSON.parse(trimmed);
    const response = parsed?.response;
    if (typeof response === "string") return response;
    if (typeof response?.message === "string") return response.message;
    if (typeof response?.content === "string") return response.content;
    if (typeof response?.text === "string") return response.text;
  } catch {
    const recoveredMessage = extractJsonEnvelopeMessage(trimmed);
    if (recoveredMessage) return recoveredMessage;
  }

  return content;
}

function extractJsonEnvelopeMessage(content: string) {
  if (!content.startsWith("{") || !content.includes('"response"')) return undefined;

  const messageKeyIndex = content.search(/"message"\s*:/);
  if (messageKeyIndex < 0) return undefined;

  const firstQuoteIndex = content.indexOf('"', content.indexOf(":", messageKeyIndex) + 1);
  if (firstQuoteIndex < 0) return undefined;

  let result = "";
  let escaped = false;
  for (let index = firstQuoteIndex + 1; index < content.length; index += 1) {
    const char = content[index];
    if (escaped) {
      result += decodeJsonStringEscape(char);
      escaped = false;
      continue;
    }

    if (char === "\\") {
      escaped = true;
      continue;
    }

    if (char === '"') {
      const remainder = content.slice(index + 1).trimStart();
      if (remainder.startsWith("}") || remainder.startsWith(",")) return result.trim();
    }

    result += char;
  }

  return result.trim() || undefined;
}

function decodeJsonStringEscape(char: string) {
  if (char === "n") return "\n";
  if (char === "r") return "\r";
  if (char === "t") return "\t";
  if (char === "b") return "\b";
  if (char === "f") return "\f";
  if (char === '"' || char === "\\" || char === "/") return char;
  return char;
}

function FormattedChatMessage({ content, color }: { content: unknown; color: string }) {
  const normalized = normalizeChatMarkdown(content);
  const lines = normalized.split(/\r?\n/);
  const elements: ReactNode[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index].trimEnd();
    if (!line.trim()) continue;

    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      elements.push(
        <div
          key={`heading-${index}`}
          style={{
            color,
            fontWeight: 700,
            fontSize: heading[1].length <= 2 ? 16 : 14,
            marginTop: elements.length ? 10 : 0,
            marginBottom: 6,
            lineHeight: 1.35
          }}
        >
          {renderInlineChatMarkdown(heading[2], color)}
        </div>
      );
      continue;
    }

    const orderedItems: string[] = [];
    const unorderedItems: string[] = [];
    let cursor = index;
    while (cursor < lines.length) {
      const ordered = lines[cursor].trim().match(/^\d+\.\s+(.+)$/);
      if (!ordered) break;
      orderedItems.push(ordered[1]);
      cursor += 1;
    }
    while (!orderedItems.length && cursor < lines.length) {
      const unordered = lines[cursor].trim().match(/^-\s+(.+)$/);
      if (!unordered) break;
      unorderedItems.push(unordered[1]);
      cursor += 1;
    }

    if (orderedItems.length || unorderedItems.length) {
      const ListTag = orderedItems.length ? "ol" : "ul";
      const items = orderedItems.length ? orderedItems : unorderedItems;
      elements.push(
        <ListTag
          key={`list-${index}`}
          style={{
            color,
            lineHeight: 1.55,
            margin: "6px 0",
            overflowWrap: "break-word",
            paddingLeft: 20,
            wordBreak: "break-word"
          }}
        >
          {items.map((item, itemIndex) => (
            <li key={`${item}-${itemIndex}`}>{renderInlineChatMarkdown(item, color)}</li>
          ))}
        </ListTag>
      );
      index = cursor - 1;
      continue;
    }

    elements.push(
      <p
        key={`paragraph-${index}`}
        style={{
          color,
          lineHeight: 1.55,
          margin: "0 0 8px",
          overflowWrap: "break-word",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word"
        }}
      >
        {renderInlineChatMarkdown(line, color)}
      </p>
    );
  }

  return <div style={{ minWidth: 0, overflowWrap: "break-word", width: "100%" }}>{elements}</div>;
}

function syncStatusColor(status?: StudyMaterial["openNotebookSyncStatus"]) {
  if (status === "failed") {
    return "red";
  }

  if (status === "pending") {
    return "gold";
  }

  return "green";
}

function getSyncStatusLabel(status?: StudyMaterial["openNotebookSyncStatus"]) {
  if (status === "failed") {
    return "Sync failed";
  }

  if (status === "pending") {
    return "Syncing...";
  }

  return "Synced";
}

function formatBytes(value?: number) {
  if (!value) {
    return "0 B";
  }

  if (value < 1024) {
    return `${value} B`;
  }

  if (value < 1024 * 1024) {
    return `${(value / 1024).toFixed(1)} KB`;
  }

  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}
